import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Component, HostBinding, HostListener, OnDestroy, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import type { EChartsOption } from 'echarts';
import { MatDialog } from '@angular/material/dialog';
import { MatTooltipModule } from '@angular/material/tooltip';
import { DashboardTitleService } from '@shared-lib/shared-services/dashboard-title.service';
import { PublishedDashboardToolbarService, PublishedDashboardAction } from '@shared-lib/shared-services/published-dashboard-toolbar.service';
import { ActivatedRoute, Router } from '@angular/router';
import { KeycloakService } from 'keycloak-angular';
import { Subscription, firstValueFrom, forkJoin, of } from 'rxjs';
import { catchError, finalize, map } from 'rxjs/operators';

import { DrillDownComponent } from '../../chart-components/drill-down/drill-down.component';
import { UnifiedTableMatrixChartComponent } from '../../chart-components/unified-table-matrix-chart/unified-table-matrix-chart.component';
import type { TableMatrixCellActionEvent, TableMatrixSaveEvent, TableMatrixWorkflowEvent } from '../../chart-components/unified-table-matrix-chart/unified-table-matrix-chart.types';
import { DvmFileUploadComponent } from '../../chart-components/dvm-file-upload/dvm-file-upload.component';
import { HorizontalBarChartComponent } from '../../chart-components/horizontal-bar-chart/horizontal-bar-chart.component';
import { UnifiedBarColumnChartComponent } from '../../chart-components/unified-bar-column-chart/unified-bar-column-chart.component';
import { UnifiedLineAreaChartComponent } from '../../chart-components/unified-line-area-chart/unified-line-area-chart.component';
import { UnifiedScatterBubbleChartComponent } from '../../chart-components/unified-scatter-bubble-chart/unified-scatter-bubble-chart.component';
import { WaterfallChartComponent } from '../../chart-components/waterfall-chart/waterfall-chart.component';
import { MatSliderComponent } from '../../global-components/mat-slider/mat-slider.component';
import { InputBoxComponent } from '../../global-components/input-box/input-box.component';
import { GlobalDropdownComponent } from '../../global-components/global-dropdown/global-dropdown.component';
import { NumberStatsComponent } from '../../global-components/number-stats/number-stats.component';
import { CapitalCasePipe } from '../../pipes/capital-case.pipe';
import { ShareDashboardDialogComponent } from '@shared-lib/self-service-visualization/dashboard-builder/share-dashboard-dialog/share-dashboard-dialog.component';
import { SelfServiceDashboardService } from '@shared-lib/self-service-visualization/shared/self-service-dashboard.service';
import { SelfServiceUiService } from '@shared-lib/self-service-visualization/shared/self-service-ui.service';
import { resolveSelfServiceApiUrl, buildSelfServiceApiUrl } from '@shared-lib/self-service-visualization/shared/self-service-api.utils';
import { readDynamicDashboardCache, writeDynamicDashboardCache } from '@shared-lib/self-service-visualization/shared/self-service-storage.utils';
import { buildRecomputedWidgetFields, buildWaterfallOption, recomputeEntryHasData, resolveRecomputeChartType } from '@shared-lib/self-service-visualization/shared/self-service-recompute.utils';
import { toSerializable } from '@shared-lib/self-service-visualization/shared/self-service-json.utils';
import { SavedSelfServiceDashboard } from '@shared-lib/self-service-visualization/shared/self-service-types';
// Same relative-import pattern side-navbar.component.ts already uses to read
// app environment flags from the shared lib (see that file for precedent).
import { environment } from '../../../../../../../../projects/my-app/src/environments/environment';

interface PublishedWidgetRec {
  id: string;
  widget: any;
  status: 'idle' | 'loading' | 'ready' | 'error';
  error?: string;
}

interface PublishedLayoutItem {
  id: string;
  colSpan: number;
  rowSpan: number;
  /** 0-based grid column start (explicit free placement — NOT an auto-flow
   *  order index). */
  x: number;
  /** 0-based grid row start. */
  y: number;
  /** True after the user explicitly resizes this tile in the published editor. */
  customSize?: boolean;
}

interface ResizeInteraction {
  id: string;
  horizontal: 'left' | 'right' | null;
  vertical: 'top' | 'bottom' | null;
  startX: number;
  startY: number;
  startColSpan: number;
  startRowSpan: number;
  /** The tile's grid x/y at drag start — a 'left'/'top' handle grows the tile
   *  by moving this edge, so the START position shifts too (the opposite edge
   *  must stay put), unlike 'right'/'bottom' which only change the span. */
  startGridX: number;
  startGridY: number;
}

@Component({
  selector: 'app-dvm-published-dashboard',
  standalone: true,
  // Do NOT add HttpClientModule: it re-provides HttpClient with an EMPTY
  // interceptor chain, so requests lose the bearer token and 401.
  imports: [CommonModule, FormsModule, MatTooltipModule, CapitalCasePipe, DrillDownComponent, UnifiedTableMatrixChartComponent, DvmFileUploadComponent, HorizontalBarChartComponent, UnifiedBarColumnChartComponent, UnifiedLineAreaChartComponent, UnifiedScatterBubbleChartComponent, WaterfallChartComponent, MatSliderComponent, InputBoxComponent, GlobalDropdownComponent, NumberStatsComponent],
  templateUrl: './dvm-published-dashboard.component.html',
  styleUrl: './dvm-published-dashboard.component.scss',
})
export class DvmPublishedDashboardComponent implements OnInit, OnDestroy {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly http = inject(HttpClient);
  private readonly keycloak = inject(KeycloakService);
  private readonly dashboardService = inject(SelfServiceDashboardService);
  private readonly ui = inject(SelfServiceUiService);
  private readonly dialog = inject(MatDialog);
  private readonly dashboardTitleService = inject(DashboardTitleService);
  private readonly publishedToolbar = inject(PublishedDashboardToolbarService);
  private readonly subs = new Subscription();

  slug = '';
  dashboardName = '';
  originalLayoutJson: Record<string, unknown> | null = null;
  widgets: PublishedWidgetRec[] = [];
  layout: PublishedLayoutItem[] = [];
  loading = true;
  loadError = '';
  editMode = false;
  isSharedReadonlyView = false;
  isOwner = false;
  private resolvedEmailMemo = '';
  private recomputingComponentIds = new Set<string>();
  private renderVersion = 0;
  private recomputeGeneration = 0;
  private tileDragId: string | null = null;
  private resizeInteraction: ResizeInteraction | null = null;
  /** Ctrl+Z / Ctrl+Shift+Z undo/redo for Edit Layout — one snapshot per
   *  discrete action (a drag-drop, a resize gesture), not per mousemove tick. */
  private layoutUndoStack: PublishedLayoutItem[][] = [];
  private layoutRedoStack: PublishedLayoutItem[][] = [];

  // ── Cross-filter (published view) ─────────────────────────────────────────
  /** Widget `type` strings that render CLIENT-SIDE from `staticData` and can
   *  publish/react to cross-filters. Generic: adding a new client-side chart
   *  (e.g. 'pie-composition') is a one-line addition here. Mirrors the builder's
   *  CLIENT_CHART_CROSS_FILTER registry. */
  private static readonly CROSS_FILTER_TYPES = new Set<string>(['bar-column', 'line-area', 'scatter-bubble']);
  /** The active cross-filter published by a chart click (null = none). Listener
   *  widgets re-render from their original rows filtered by column IN values. */
  private activeCrossFilter: { column: string; values: string[]; sourceId: string } | null = null;
  /** Original (unfiltered) staticData rows per widget id, captured on load so a
   *  filter can be reversed. */
  private crossFilterOriginalRows = new Map<string, Array<Record<string, unknown>>>();
  /** Filter carried in via table→dashboard navigation query params; applied to
   *  the named target components once the dashboard has loaded. */
  private incomingNavFilter: { filterKey: string; filterValue: string; targetComponents: string[] } | null = null;

  @HostBinding('class.dvm-published-host') hostClass = true;

  /** Margin Estimator branch: this is a read-only, dashboard-only experience —
   *  no route back into the builder, no sharing UI, no "DVM Dashboard" kicker. */
  get marginEstimatorMode(): boolean {
    const flag = (environment as any).MARGIN_ESTIMATOR_MODE;
    return flag === true || flag === 'true';
  }

  get canShowOwnerActions(): boolean {
    return !this.marginEstimatorMode && this.isOwner && !this.isSharedReadonlyView;
  }

  get viewItems(): Array<{ rec: PublishedWidgetRec; item: PublishedLayoutItem; renderKey: string }> {
    return this.layout
      .map((item) => {
        const rec = this.widgets.find((widget) => widget.id === item.id);
        return rec ? { rec, item, renderKey: item.id + ':' + this.renderVersion } : null;
      })
      .filter((entry): entry is { rec: PublishedWidgetRec; item: PublishedLayoutItem; renderKey: string } => !!entry);
  }

  ngOnInit(): void {

    this.subs.add(
      this.route.paramMap.subscribe((params) => {
        this.slug = params.get('slug') || '';
        const qp = this.route.snapshot.queryParamMap;
        this.isSharedReadonlyView = qp.get('shared') === '1';
        // Incoming table→dashboard navigation filter (see onTableNavigate on the
        // source dashboard). Applied to the named target components after load.
        const filterKey = qp.get('filterKey') || '';
        const filterValue = qp.get('filterValue');
        this.incomingNavFilter = filterKey && filterValue != null
          ? {
              filterKey,
              filterValue,
              targetComponents: (qp.get('filterTargets') || '').split(',').map((s) => s.trim()).filter(Boolean),
            }
          : null;
        this.loadPublishedDashboard();
      }),
    );

    // The settings gear + its actions live in the shared TOP toolbar (not an
    // in-page bar), so listen for the menu items it fires there.
    this.subs.add(
      this.publishedToolbar.action$.subscribe((action) => this.handleToolbarAction(action)),
    );
    this.syncToolbarState();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
    // Stop showing this dashboard's name in the shared top toolbar once we
    // navigate away — otherwise it lingers beside the company logo on the
    // next screen. Same for the settings gear/actions.
    this.dashboardTitleService.clearDashboardTitle();
    this.publishedToolbar.clear();
  }

  /** Push the current toolbar state up to the shared top-bar settings menu so
   *  it renders the right actions in the right enabled/edit state. Called
   *  whenever editMode / widget count / ownership changes. */
  private syncToolbarState(): void {
    this.publishedToolbar.setState({
      editMode: this.editMode,
      hasWidgets: this.widgets.length > 0,
      showOwnerActions: this.canShowOwnerActions,
    });
  }

  /** Runs a settings action fired from the top-bar menu. */
  private handleToolbarAction(action: PublishedDashboardAction): void {
    switch (action) {
      case 'edit': this.enterEditMode(); break;
      case 'reset': this.resetDashboardSelections(); break;
      case 'cancel': this.cancelEditMode(); break;
      case 'save': void this.saveLayout(); break;
      case 'back': this.backToBuilder(); break;
      case 'share': this.shareThisDashboard(); break;
      case 'duplicate': void this.duplicateDashboard(); break;
    }
  }

  /** Toolbar chip text confirming an incoming navigation filter arrived (proves
   *  the clicked value reached this dashboard). Empty when there's no filter. */
  get incomingNavFilterLabel(): string {
    const nav = this.incomingNavFilter;
    return nav ? `${nav.filterKey} = ${nav.filterValue}` : '';
  }

  /** Clear the incoming filter chip and restore base data (same as Reset). */
  clearNavigationFilter(): void {
    this.incomingNavFilter = null;
    if (this.originalLayoutJson) {
      this.recomputingComponentIds = new Set();
      this.renderLayout(this.originalLayoutJson);
    }
    // Drop the query params from the URL so a refresh doesn't re-apply them.
    this.router.navigate([], { relativeTo: this.route, queryParams: {} });
  }

  backToBuilder(): void {
    if (!this.slug) return;
    if (this.originalLayoutJson) writeDynamicDashboardCache(this.slug, this.originalLayoutJson);
    this.router.navigate(['/dvm-layout-builder'], {
      queryParams: {
        view: 'layout',
        dashboard: this.slug,
        dashboardId: this.dashboardName || this.slug,
        preview: 'true',
      },
      state: {
        selfServiceView: 'layout',
        dashboardSlug: this.slug,
        dashboardId: this.dashboardName || this.slug,
        layoutJson: this.originalLayoutJson,
        preview: true,
      },
    });
  }

  shareThisDashboard(): void {
    if (!this.slug || !this.originalLayoutJson) return;
    this.dialog.open(ShareDashboardDialogComponent, {
      width: 'min(560px, 94vw)',
      maxWidth: '94vw',
      autoFocus: false,
      restoreFocus: false,
      panelClass: 'ss-share-dialog-panel',
      data: {
        shareLink: `${window.location.origin}/dvm-published/${this.slug}?shared=1`,
        shareSlug: this.slug,
        layoutJson: this.originalLayoutJson,
        dashboardName: this.dashboardName || this.slug,
      },
    });
  }

  /** "Duplicate to new dashboard": clone every chart on THIS dashboard into a
   *  brand-new, fully independent one under a name the user types — publish
   *  (recompute/edit/reset/share) all work on the copy exactly like any other
   *  dashboard from here on, since it's a genuine new `dashboard_layouts` row,
   *  not a reference back to this one. */
  async duplicateDashboard(): Promise<void> {
    if (!this.originalLayoutJson) return;
    const suggested = `${this.dashboardName || this.slug} copy`;
    const typed = window.prompt('Name for the new dashboard:', suggested);
    if (typed == null) return; // cancelled
    const newName = typed.trim();
    if (!newName) return;

    const cloned = this.deepCloneRecord(this.originalLayoutJson);
    const withFreshIds = this.remapComponentIdsForCopy(cloned);
    withFreshIds['dashboard-name'] = newName;
    withFreshIds['name'] = newName;
    // Force a fresh dashboard_id — never reuse the source's, or publish would
    // update the SOURCE row instead of inserting this as a new one.
    delete withFreshIds['dashboardId'];
    delete withFreshIds['publishedAt'];

    const newSlug = this.buildCopySlug(newName);
    try {
      const email = await this.resolveCurrentEmail();
      await firstValueFrom(this.dashboardService.publishLayoutToApi(withFreshIds, email, newSlug));
      writeDynamicDashboardCache(newSlug, withFreshIds);
      this.ui.showSuccess(`"${newName}" created with a copy of every chart.`);
      window.dispatchEvent(new CustomEvent('dvm:dashboard-published', { detail: { dashboardId: newSlug, name: newName } }));
      this.router.navigate(['/dvm-published', newSlug]);
    } catch (error) {
      console.error('[dvm-published] duplicate dashboard failed:', error);
      this.ui.showError('Could not create the duplicate — please try again.');
    }
  }

  private buildCopySlug(name: string): string {
    const base = String(name || '').trim().toLowerCase().replace(/[^a-z0-9_-]+/g, '-').replace(/-+/g, '-').replace(/^-|-$/g, '') || 'untitled-dashboard';
    return `${base}-${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;
  }

  /** Mint a fresh id for every componentId/widgetId/id found anywhere in the
   *  cloned layout, then rewrite EVERY reference to it (input/dropdown target
   *  lists, layout.items, group-card children, etc.) via an exact quoted-string
   *  swap on the serialized JSON — simpler and more complete than hand-walking
   *  every place an id could be referenced, and safe because these ids are
   *  distinctively prefixed (CMP-/tile-/GRP-/dvm-tile-…), so an accidental
   *  collision with unrelated user text is effectively impossible. Without
   *  this, the copy's charts would share ids with the source's — editing or
   *  recomputing one could silently clobber the other's saved component config. */
  private remapComponentIdsForCopy(layoutJson: Record<string, unknown>): Record<string, unknown> {
    const ids = new Set<string>();
    const collect = (node: unknown): void => {
      if (Array.isArray(node)) { node.forEach(collect); return; }
      if (!node || typeof node !== 'object') return;
      const record = node as Record<string, unknown>;
      for (const key of ['componentId', 'widgetId', 'id']) {
        const value = record[key];
        if (typeof value === 'string' && value) ids.add(value);
      }
      Object.values(record).forEach(collect);
    };
    collect(layoutJson);

    const suffix = () => Math.random().toString(36).slice(2, 7);
    let json = JSON.stringify(layoutJson);
    ids.forEach((id) => {
      json = json.split(`"${id}"`).join(`"${id}-copy-${suffix()}"`);
    });
    return JSON.parse(json);
  }

  enterEditMode(): void {
    this.editMode = true;
    // Fresh undo/redo history per edit session — nothing from a previous
    // Edit Layout visit should be un-doable after Save/Cancel closed it.
    this.layoutUndoStack = [];
    this.layoutRedoStack = [];
    this.syncToolbarState();
  }

  cancelEditMode(): void {
    this.editMode = false;
    this.tileDragId = null;
    this.resizeInteraction = null;
    this.layout = this.buildLayoutFromJson(this.originalLayoutJson, this.widgets);
    this.layoutUndoStack = [];
    this.layoutRedoStack = [];
    this.syncToolbarState();
  }

  /** Snapshot the layout BEFORE a discrete edit (a drag-drop, or the START of a
   *  resize gesture — not every mousemove tick) so Ctrl+Z has something to
   *  restore. A fresh edit always clears redo — you can't redo past a NEW
   *  change, same as any standard undo stack. */
  private snapshotLayoutForUndo(): void {
    this.layoutUndoStack.push(this.layout.map((item) => ({ ...item })));
    if (this.layoutUndoStack.length > 50) this.layoutUndoStack.shift();
    this.layoutRedoStack = [];
  }

  /** Ctrl+Z while editing the layout — undo the last drag/resize. Ignored
   *  outside Edit Layout and while typing in a text field (so it doesn't
   *  fight a text input's own native undo, e.g. the dashboard name). */
  @HostListener('window:keydown', ['$event'])
  onLayoutUndoRedoKeydown(event: KeyboardEvent): void {
    if (!this.editMode) return;
    if (!(event.ctrlKey || event.metaKey) || event.key.toLowerCase() !== 'z') return;
    const target = event.target as HTMLElement | null;
    const tag = target?.tagName;
    if (tag === 'INPUT' || tag === 'TEXTAREA' || target?.isContentEditable) return;
    event.preventDefault();
    if (event.shiftKey) this.redoLayoutChange();
    else this.undoLayoutChange();
  }

  undoLayoutChange(): void {
    const previous = this.layoutUndoStack.pop();
    if (!previous) return;
    this.layoutRedoStack.push(this.layout.map((item) => ({ ...item })));
    this.layout = previous;
    this.notifyChartsAboutResize();
  }

  redoLayoutChange(): void {
    const next = this.layoutRedoStack.pop();
    if (!next) return;
    this.layoutUndoStack.push(this.layout.map((item) => ({ ...item })));
    this.layout = next;
    this.notifyChartsAboutResize();
  }

  async saveLayout(): Promise<void> {
    // eslint-disable-next-line no-console
    console.log('[dvm-published] saveLayout() called', { slug: this.slug, layout: this.layout });
    try {
      const nextLayoutJson = this.buildPersistedLayoutJson();
      // eslint-disable-next-line no-console
      console.log('[dvm-published] buildPersistedLayoutJson() ->', nextLayoutJson);
      if (!nextLayoutJson) {
        // eslint-disable-next-line no-console
        console.log('[dvm-published] no originalLayoutJson — nothing to publish, aborting');
        return;
      }
      await this.publishLayoutJson(nextLayoutJson, { broadcast: true });
      this.ui.showSuccess('Layout saved.');
    } catch (error) {
      // This used to be a console.error only — the layout edit was silently
      // dropped on any backend failure (expired token, network blip, 500…)
      // while the UI still dropped out of edit mode looking like it saved.
      // Surface it so the user knows to retry instead of assuming it worked.
      console.error('[dvm-published] could not persist edited layout:', error);
      const detail = error instanceof Error ? error.message : '';
      this.ui.showError(detail ? `Could not save the layout: ${detail}` : 'Could not save the layout — please try again.');
    } finally {
      this.editMode = false;
      this.syncToolbarState();
    }
  }

  /** Shared network round-trip behind `saveLayout()` (explicit "Save Layout"
   *  button) and `persistInputOverride()` (auto-persist after a slider/input
   *  recompute) — both need the exact same save+publish call, just triggered
   *  differently and with different user-facing feedback. */
  private async publishLayoutJson(
    nextLayoutJson: Record<string, unknown>,
    // `dvm:dashboard-published` triggers a full sidebar rebuild + API re-query.
    // Right after an explicit Save; a page-freezing waste after auto-persist,
    // which only rewrites this dashboard's own row. Off by default.
    options: { broadcast?: boolean } = {},
  ): Promise<void> {
    const dashboardName = this.dashboardName || this.slug || 'Untitled dashboard';
    const now = new Date().toISOString();
    const savedDashboard: SavedSelfServiceDashboard = {
      id: String(nextLayoutJson['id'] ?? nextLayoutJson['dashboardId'] ?? this.slug ?? this.createDashboardId()),
      name: dashboardName,
      createdAt: String(nextLayoutJson['createdAt'] ?? now),
      updatedAt: now,
      items: [],
      layout: this.buildSavedDashboardLayout(nextLayoutJson),
      status: 'published',
      publishedAt: String(nextLayoutJson['publishedAt'] ?? now),
      config: nextLayoutJson,
    };
    await firstValueFrom(this.dashboardService.saveDashboard(savedDashboard));
    const email = await this.resolveCurrentEmail();
    // This dashboard is already published (we're viewing/editing it here), so
    // look up its existing row's uuid — present → update that row; absent →
    // this email doesn't own an existing row yet, insert a new one. Matched
    // by `this.slug` (the exact dashboard_id the dashboard was published
    // under, from the route) — NOT the display name, which may not be unique
    // and doesn't include the id's uniqueness suffix.
    const existingRecord = email
      ? await firstValueFrom(this.dashboardService.findOwnedDashboardLayoutRecord(this.slug, email))
      : null;
    const publishUrl = await this.resolvePublishApi(nextLayoutJson);
    const publishPayload = this.buildPublishEnvelope(nextLayoutJson, email, this.slug, existingRecord?.uuid ?? null);
    if (publishUrl) {
      await firstValueFrom(this.http.post(publishUrl, publishPayload));
    } else {
      await firstValueFrom(this.dashboardService.publishLayoutToApi(nextLayoutJson, email, this.slug));
    }
    this.originalLayoutJson = nextLayoutJson;
    writeDynamicDashboardCache(this.slug, nextLayoutJson);
    if (options.broadcast) {
      window.dispatchEvent(new CustomEvent('dvm:dashboard-published'));
    }
  }

  /** Debounced auto-persist fired after a slider/input-box change recomputes
   *  its target KPIs — without this, `recomputeChartsOnOpen`/reload always
   *  re-pulled the original business-computed values, so a user's override
   *  only ever lived in `this.widgets` for the current tab and silently reset
   *  on refresh. Debounced (not saved on every intermediate value) because a
   *  number input box emits on every keystroke, not just on release. Owner-only
   *  — a shared read-only viewer nudging a slider must not rewrite the
   *  published dashboard everyone else sees. */
  private autoPersistHandle: ReturnType<typeof setTimeout> | null = null;
  private autoPersistGeneration = 0;
  private scheduleAutoPersistOverride(): void {
    if (!this.isOwner || this.isSharedReadonlyView) return;
    if (this.autoPersistHandle) clearTimeout(this.autoPersistHandle);
    this.autoPersistHandle = setTimeout(() => {
      this.autoPersistHandle = null;
      void this.persistInputOverride();
    }, 600);
  }

  private async persistInputOverride(): Promise<void> {
    const generation = ++this.autoPersistGeneration;
    try {
      const nextLayoutJson = this.buildPersistedLayoutJson();
      if (!nextLayoutJson) return;
      await this.publishLayoutJson(nextLayoutJson);
      // A newer slider/input change (and its own auto-persist) superseded this
      // one while the request was in flight — its result already reflects the
      // latest state, so don't let this stale response undo it.
      if (generation !== this.autoPersistGeneration) return;
    } catch (error) {
      console.warn('[dvm-published] auto-persist of input override failed:', error);
    }
  }
  /** "Reset" used to just replay `this.originalLayoutJson` — but that gets
   *  overwritten with the LAST auto-persisted state on every slider/dropdown
   *  change (see `publishLayoutJson`), so once any input had been touched even
   *  once, Reset silently redrew that same override back at you: no API call,
   *  no actual return to default. Drive every input widget back to ITS OWN
   *  `defaultValue` through the real compute call instead — the exact same
   *  path `onInputWidgetChange` already uses for a live edit — so Reset
   *  visibly recomputes every dependent chart from true default data (and
   *  persists that as the new saved state, same as any other input change). */
  resetDashboardSelections(): void {
    if (!this.originalLayoutJson) return;
    this.editMode = false;
    this.tileDragId = null;
    this.resizeInteraction = null;

    // EVERY selection the user can apply has to go, not just input values.
    // Nulling `incomingNavFilter` alone (all this used to do) hid the toolbar
    // chip and nothing else.
    const hadNavFilter = !!this.incomingNavFilter;
    this.incomingNavFilter = null;
    // A click-driven cross-filter is a selection too — clearing it restores each
    // reacting chart's unfiltered rows (and its listening waterfalls).
    if (this.activeCrossFilter) {
      this.activeCrossFilter = null;
      this.applyPublishedCrossFilter();
    }
    // Dropdown-driven client-side filters (bar-column/line-area/scatter-bubble
    // rows filtered locally) are selections too — restore the full rows.
    if (this.activeDropdownFilters.size) {
      this.activeDropdownFilters.clear();
      this.applyDropdownFiltersToClientWidgets();
    }
    // applyIncomingNavigationFilter WRITES filterKey/filterValue/filters INTO
    // each target chart's own payload, so the filter outlived Reset entirely —
    // and rode along into the auto-persisted override. Strip it, then recompute
    // those charts below so they come back with unfiltered data.
    const unfilteredChartRecs = this.stripSelectionFiltersFromPayloads();
    if (hadNavFilter) {
      // Same URL cleanup clearNavigationFilter() does. Without it a refresh
      // re-applies the very filter Reset just cleared.
      this.router.navigate([], { relativeTo: this.route, queryParams: {} });
    }

    // NOT calling renderLayout() here: it kicks off its OWN async recompute
    // (maybeRecomputeChartsOnOpen) under a freshly-bumped `recomputeGeneration`
    // — then looping onInputWidgetChange() per input right after bumped that
    // SAME shared counter again on every call. By the time either request
    // actually came back (fast — this wasn't a timeout), its stale-generation
    // guard (`generation !== this.recomputeGeneration`) silently discarded the
    // response, so `setWidgetStatus(..., 'ready')` never ran for the charts
    // renderLayout had just marked 'loading' — stuck spinner forever, even
    // though the network tab showed the calls completing fine. Reset each
    // input's own value directly and batch every recompute under ONE shared
    // generation instead (below), so nothing invalidates anything else's
    // response.
    const inputRecs = this.widgets.filter((rec) => this.isInputWidget(rec.widget));

    // Reset EVERY input's own displayed value first — a slider's handle/number
    // box reads `widget.data.data.rangeStart/rangeEnd` and an input-box reads
    // `widget.value`, NEITHER of which lives under `widget.payload` (that's
    // only the compute-call body). Mutating payload alone — what this used to
    // do — never touched what the slider/input actually renders, so the
    // control itself looked unchanged even though its target charts updated.
    // Building a NEW widget object (not mutating in place) and swapping it
    // into `this.widgets` also matters: the child component's `[data]` input
    // only re-syncs (ngOnChanges) on a REFERENCE change, not a deep mutation.
    const resetJobs = inputRecs.map((rec) => {
      const widget = rec.widget;
      const type = String(widget?.type ?? '').toLowerCase();
      const rawValue = this.defaultValueForInputWidget(widget);
      const value = rawValue && typeof rawValue === 'object' && 'end' in (rawValue as Record<string, unknown>)
        ? (rawValue as Record<string, unknown>)['end']
        : rawValue;
      const payload = { ...(widget?.payload ?? {}), value };
      // Dropdown targets are rebuilt from the CURRENT chart set, same as a
      // live edit (onInputWidgetChange) — it's a global filter, not a
      // pre-selected target list.
      if (type === 'dropdown') {
        const rebuilt = this.widgets
          .filter((r) => this.isChartWidget(r.widget) && String(r.widget?.componentId ?? '') !== String(widget?.componentId ?? ''))
          .map((r) => ({ componentId: String(r.widget?.componentId ?? ''), label: r.widget?.title ?? '', field: payload['field'] ?? '' }))
          .filter((target) => !!target.componentId);
        if (rebuilt.length) payload['targets'] = rebuilt;
        else if (!Array.isArray(payload['targets'])) payload['targets'] = [];
      }
      const nextWidget = {
        ...widget,
        payload,
        ...(type === 'slider'
          ? { data: { ...(widget?.data ?? {}), data: { ...(widget?.data?.data ?? {}), rangeStart: value, rangeEnd: value } } }
          : type === 'input-box' ? { value } : {}),
      };
      return { id: rec.id, widget: nextWidget, payload, targetIds: this.extractTargetIds(payload['targets']) };
    });

    const jobById = new Map(resetJobs.map((job) => [job.id, job]));
    this.widgets = this.widgets.map((rec) => (jobById.has(rec.id) ? { ...rec, widget: jobById.get(rec.id)!.widget } : rec));

    const jobs = resetJobs.filter((job) => job.targetIds.length);

    const allTargetIds = new Set<string>();
    jobs.forEach((job) => job.targetIds.forEach((id) => allTargetIds.add(id)));
    // A chart that was carrying a navigation/cross filter but isn't any input's
    // target still needs one plain recompute to come back unfiltered — nothing
    // else would ever re-request it.
    const filterClearRecs = unfilteredChartRecs.filter(
      (rec) => !allTargetIds.has(String(rec.widget?.componentId ?? '').trim()),
    );

    if (!jobs.length && !filterClearRecs.length) {
      this.recomputingComponentIds = new Set();
      return;
    }

    const busyIds = new Set<string>(allTargetIds);
    filterClearRecs.forEach((rec) => {
      const id = String(rec.widget?.componentId ?? '').trim();
      if (id) busyIds.add(id);
    });
    this.recomputingComponentIds = busyIds;
    const generation = ++this.recomputeGeneration;

    // ONE batch under ONE generation (see the note above on stale-generation
    // guards eating responses). The two kinds of request need different apply
    // calls, so each resolves to a thunk that applies its own result and reports
    // whether anything landed — that keeps them in the same forkJoin.
    const inputRequests = jobs.map((job) =>
      this.http.post<Record<string, unknown>>(this.computeUrlForWidget(job.widget), job.payload).pipe(
        map((response) => () => {
          this.applyRecomputeResults(response, job.targetIds);
          return true;
        }),
        catchError((error) => {
          console.warn('[dvm-published] reset recompute failed:', error);
          return of(() => false);
        }),
      ),
    );

    const filterClearRequests = filterClearRecs.map((rec) =>
      this.http.post<Record<string, unknown>>(this.computeUrlForWidget(rec.widget), rec.widget?.payload ?? {}).pipe(
        map((response) => () => {
          const applied = this.applyComputeResultToWidget(rec.id, rec.widget, response);
          this.setWidgetStatus(rec.id, 'ready');
          return applied;
        }),
        catchError((error) => {
          console.warn('[dvm-published] reset filter-clear recompute failed:', error);
          return of(() => {
            this.setWidgetStatus(rec.id, 'ready');
            return false;
          });
        }),
      ),
    );

    this.subs.add(
      forkJoin([...inputRequests, ...filterClearRequests]).pipe(
        finalize(() => { if (generation === this.recomputeGeneration) this.recomputingComponentIds = new Set(); }),
      ).subscribe((appliers) => {
        if (generation !== this.recomputeGeneration) return;
        let applied = false;
        appliers.forEach((apply) => { if (apply()) applied = true; });
        // One persist for the whole reset, not one per input.
        if (applied) this.scheduleAutoPersistOverride();
      }),
    );
  }

  /** Remove navigation-filter / cross-filter keys from every CHART's compute
   *  payload, returning the records that were carrying one (already swapped into
   *  `this.widgets`). These keys are written into the payload itself — see
   *  applyIncomingNavigationFilter and the cross-filter recomputes — so they
   *  persist across every later compute until something takes them out. */
  private stripSelectionFiltersFromPayloads(): PublishedWidgetRec[] {
    const touched: PublishedWidgetRec[] = [];
    this.widgets = this.widgets.map((rec) => {
      if (!this.isChartWidget(rec.widget)) return rec;
      const payload = rec.widget?.payload as Record<string, unknown> | undefined;
      if (!payload) return rec;
      if (!('filterKey' in payload) && !('filterValue' in payload) && !('filters' in payload)) return rec;
      const next = { ...payload };
      delete next['filterKey'];
      delete next['filterValue'];
      delete next['filters'];
      const nextRec = { ...rec, widget: { ...rec.widget, payload: next } };
      touched.push(nextRec);
      return nextRec;
    });
    return touched;
  }

  /** Reconstruct the SAME default-value shape the builder gives each input
   *  type at generate time (see DvmLayoutBuilderComponent.buildDropdownTile /
   *  buildInputTile): sliders/input boxes store a plain default; dropdowns
   *  always bind an ARRAY (GlobalDropdownComponent.writeValue), single-select
   *  wrapping its lone default into a one-item array. */
  private defaultValueForInputWidget(widget: any): unknown {
    const defaultValue = widget?.payload?.defaultValue;
    const type = String(widget?.type ?? '').toLowerCase();
    if (type === 'dropdown') {
      if (Array.isArray(defaultValue)) return defaultValue;
      return defaultValue ? [defaultValue] : [];
    }
    // No default was configured in the builder. A slider's own seed value is its
    // range minimum (buildInputControlPayload uses the same fallback), and
    // pushing null instead recomputed the charts on null while the slider's
    // handle stayed put — MatSlider ignores a null pushed value.
    if (defaultValue === null || defaultValue === undefined) {
      const min = Number((widget?.payload?.range as Record<string, unknown> | undefined)?.['min']);
      return type === 'slider' && Number.isFinite(min) ? min : null;
    }
    return defaultValue;
  }

  onTileDragStart(id: string, event: DragEvent): void {
    if (!this.editMode) return;
    this.tileDragId = id;
    event.dataTransfer?.setData('text/plain', id);
    if (event.dataTransfer) event.dataTransfer.effectAllowed = 'move';
  }

  onTileDragOver(event: DragEvent): void {
    if (!this.editMode || !this.tileDragId) return;
    event.preventDefault();
    event.stopPropagation();
    if (event.dataTransfer) event.dataTransfer.dropEffect = 'move';
  }

  onCanvasDragOver(event: DragEvent): void {
    if (!this.editMode || !this.tileDragId) return;
    event.preventDefault();
    if (event.dataTransfer) event.dataTransfer.dropEffect = 'move';
  }

  /** Dropped directly on another tile — swap the two tiles' positions rather
   *  than computing a pixel-precise cell under the cursor (dropping ON a tile
   *  unambiguously means "I want to take that tile's spot"). */
  onTileDrop(targetId: string, event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    if (!this.editMode) return;
    const sourceId = this.tileDragId || event.dataTransfer?.getData('text/plain') || '';
    if (sourceId && sourceId !== targetId) {
      this.snapshotLayoutForUndo();
      this.swapTilePositions(sourceId, targetId);
    }
    this.tileDragId = null;
  }

  /** Dropped on empty canvas background — free placement: move the tile to
   *  whatever grid cell is under the cursor. If that cell is actually occupied
   *  by another tile (the empty-looking area was really inside its footprint,
   *  e.g. a gap next to a short tile under a tall one), swap with it instead
   *  of silently overlapping two tiles on top of each other. */
  onCanvasDrop(event: DragEvent): void {
    event.preventDefault();
    if (!this.editMode) return;
    const sourceId = this.tileDragId || event.dataTransfer?.getData('text/plain') || '';
    const source = this.layout.find((item) => item.id === sourceId);
    const canvas = event.currentTarget as HTMLElement | null;
    if (!source || !canvas) { this.tileDragId = null; return; }

    const cell = this.gridCellFromEvent(canvas, event, source.colSpan, source.rowSpan);
    const occupant = this.layout.find((item) => item.id !== source.id && this.rectsOverlap(item, cell));
    this.snapshotLayoutForUndo();
    if (occupant) {
      this.swapTilePositions(source.id, occupant.id);
    } else {
      this.layout = this.layout.map((item) => (item.id === source.id ? { ...item, x: cell.x, y: cell.y, customSize: true } : item));
    }
    this.tileDragId = null;
  }

  private swapTilePositions(sourceId: string, targetId: string): void {
    const source = this.layout.find((item) => item.id === sourceId);
    const target = this.layout.find((item) => item.id === targetId);
    if (!source || !target) return;
    this.layout = this.layout.map((item) => {
      if (item.id === source.id) return { ...item, x: target.x, y: target.y, customSize: true };
      if (item.id === target.id) return { ...item, x: source.x, y: source.y, customSize: true };
      return item;
    });
  }

  /** True when two spans (a tile's own x/y/colSpan/rowSpan, or a candidate
   *  drop rectangle of the same shape) overlap on the grid. */
  private rectsOverlap(
    a: { x: number; y: number; colSpan: number; rowSpan: number },
    b: { x: number; y: number; colSpan: number; rowSpan: number },
  ): boolean {
    return a.x < b.x + b.colSpan && a.x + a.colSpan > b.x && a.y < b.y + b.rowSpan && a.y + a.rowSpan > b.y;
  }

  /** Resolves the grid cell (0-based column/row) under a drop's cursor
   *  position, from the canvas's OWN rendered track sizes — reads the actual
   *  computed `grid-template-columns` track width rather than recomputing it
   *  from padding/gap assumptions, so it stays correct if the CSS ever changes.
   *  Clamped so the dragged tile's own span never runs off the 12-column grid
   *  (rows have no upper bound — the grid auto-creates more as needed). */
  private gridCellFromEvent(
    canvas: HTMLElement,
    event: DragEvent,
    colSpan: number,
    rowSpan: number,
  ): { x: number; y: number; colSpan: number; rowSpan: number } {
    const rect = canvas.getBoundingClientRect();
    const styles = getComputedStyle(canvas);
    const paddingLeft = parseFloat(styles.paddingLeft) || 0;
    const paddingTop = parseFloat(styles.paddingTop) || 0;
    const colGap = parseFloat(styles.columnGap) || 14;
    const rowGap = parseFloat(styles.rowGap) || 14;
    const trackWidths = styles.gridTemplateColumns.split(' ').map((track) => parseFloat(track)).filter((n) => Number.isFinite(n));
    const colWidth = trackWidths.length ? trackWidths[0] : (rect.width - paddingLeft * 2 - 11 * colGap) / 12;
    const rowHeight = 48;

    const relX = event.clientX - rect.left - paddingLeft;
    const relY = event.clientY - rect.top - paddingTop + canvas.scrollTop;
    const rawCol = Math.floor(relX / (colWidth + colGap));
    const rawRow = Math.floor(relY / (rowHeight + rowGap));

    return {
      x: this.clamp(rawCol, 0, Math.max(0, 12 - colSpan)),
      y: Math.max(0, rawRow),
      colSpan,
      rowSpan,
    };
  }

  onTileDragEnd(): void {
    this.tileDragId = null;
  }

  startTileResize(id: string, event: MouseEvent, horizontal: 'left' | 'right' | null = 'right', vertical: 'top' | 'bottom' | null = 'bottom'): void {
    if (!this.editMode) return;
    event.preventDefault();
    event.stopPropagation();
    const item = this.layout.find((layoutItem) => layoutItem.id === id);
    if (!item) return;
    this.snapshotLayoutForUndo();
    this.resizeInteraction = {
      id,
      horizontal,
      vertical,
      startX: event.clientX,
      startY: event.clientY,
      startColSpan: item.colSpan,
      startRowSpan: item.rowSpan,
      startGridX: item.x,
      startGridY: item.y,
    };
  }

  /** In-flight drag on a group-card MEMBER's bottom edge (per-member height,
   *  Edit Layout only) — independent of the tile-level resizeInteraction. */
  private memberResizeInteraction: { groupId: string; childId: string; startY: number; startHeight: number } | null = null;

  startMemberResize(groupRec: PublishedWidgetRec, childId: string, event: MouseEvent): void {
    if (!this.editMode) return;
    event.preventDefault();
    event.stopPropagation();
    const host = (event.target as HTMLElement | null)?.closest('.group-card-member') as HTMLElement | null;
    const startHeight = this.memberHeightFor(groupRec.widget, childId) ?? host?.offsetHeight ?? 160;
    this.snapshotLayoutForUndo();
    this.memberResizeInteraction = { groupId: groupRec.id, childId, startY: event.clientY, startHeight };
  }

  /** A member's user-dragged height in px, or null when it auto-sizes.
   *  Stored on the group-card widget itself (`memberHeights[childId]`) so
   *  buildPersistedLayoutJson's widget snapshot persists it with Save Layout. */
  memberHeightFor(groupWidget: any, childId: string): number | null {
    const value = Number(groupWidget?.memberHeights?.[childId]);
    return Number.isFinite(value) && value > 0 ? value : null;
  }

  @HostListener('window:mousemove', ['$event'])
  onWindowMouseMove(event: MouseEvent): void {
    const memberInteraction = this.memberResizeInteraction;
    if (memberInteraction) {
      const rec = this.widgets.find((widget) => widget.id === memberInteraction.groupId);
      if (rec) {
        // Floor keeps a header strip + a sliver of content grabbable — a
        // member dragged to 0 would be impossible to resize back.
        const next = Math.max(48, Math.round(memberInteraction.startHeight + (event.clientY - memberInteraction.startY)));
        rec.widget.memberHeights = { ...(rec.widget.memberHeights ?? {}), [memberInteraction.childId]: next };
      }
      return;
    }
    const interaction = this.resizeInteraction;
    if (!interaction) return;
    const rawColDelta = Math.round((event.clientX - interaction.startX) / 90);
    const rawRowDelta = Math.round((event.clientY - interaction.startY) / 48);
    const colDelta = interaction.horizontal === 'left' ? -rawColDelta : interaction.horizontal === 'right' ? rawColDelta : 0;
    const rowDelta = interaction.vertical === 'top' ? -rawRowDelta : interaction.vertical === 'bottom' ? rawRowDelta : 0;
    this.layout = this.layout.map((item) => {
      if (item.id !== interaction.id) return item;
      const nextColSpan = this.clamp(interaction.startColSpan + colDelta, this.minColSpanForItem(item.id), 12);
      const nextRowSpan = this.clamp(interaction.startRowSpan + rowDelta, this.minRowSpanForItem(item.id), 18);
      // A 'left'/'top' handle grows the tile by moving THAT edge outward — the
      // opposite (right/bottom) edge must stay fixed, so the grid x/y start
      // shifts by exactly the applied (post-clamp) span change, in the
      // opposite direction. 'right'/'bottom' handles only change the span.
      const appliedColGrowth = nextColSpan - interaction.startColSpan;
      const appliedRowGrowth = nextRowSpan - interaction.startRowSpan;
      const nextX = interaction.horizontal === 'left' ? Math.max(0, interaction.startGridX - appliedColGrowth) : item.x;
      const nextY = interaction.vertical === 'top' ? Math.max(0, interaction.startGridY - appliedRowGrowth) : item.y;
      return { ...item, colSpan: nextColSpan, rowSpan: nextRowSpan, x: nextX, y: nextY, customSize: true };
    });
    this.notifyChartsAboutResize();
  }

  @HostListener('window:mouseup')
  onWindowMouseUp(): void {
    this.resizeInteraction = null;
    this.memberResizeInteraction = null;
  }

  isWidgetBusy(widget: any): boolean {
    const id = String(widget?.componentId ?? '');
    return !!id && this.recomputingComponentIds.has(id);
  }

  /** Any recompute in flight — drives the single dashboard-level progress bar,
   *  so the user gets ONE clear "updating" signal instead of reading it off
   *  however many tiles happen to be targeted. */
  get isAnyWidgetBusy(): boolean {
    return this.recomputingComponentIds.size > 0;
  }

  /** Which skeleton shape to draw while a tile first loads, so the placeholder
   *  roughly matches what's about to render (a chart's plot area vs a table's
   *  rows vs a KPI's single figure) instead of one generic block everywhere.
   *  The CSS keys off this via [data-variant]. */
  skeletonVariant(widget: any): string {
    const type = String(widget?.type ?? '').toLowerCase();
    if (type === 'kpi-card') return 'kpi';
    if (type === 'drill-down' || type === 'table-matrix') return 'table';
    if (this.isInputWidget(widget)) return 'input';
    return 'chart';
  }

  private loadPublishedDashboard(): void {
    this.loading = true;
    this.loadError = '';
    this.widgets = [];
    this.layout = [];

    if (!this.slug) {
      this.loading = false;
      this.loadError = 'Dashboard slug is missing.';
      return;
    }

    // Read the cache ONLY if the API fails — decompressing it is a synchronous
    // pure-JS LZ pass over the whole layout, pure waste on a successful load.
    this.resolveCurrentEmail().then((email) => {
      this.confirmOwnership(email);
      this.subs.add(
        this.dashboardService.getPublishedLayoutBySlug(this.slug, email).pipe(
          catchError(() => of(null)),
        ).subscribe((layoutJson) => {
          const resolved = layoutJson
            || readDynamicDashboardCache<Record<string, unknown> | null>(this.slug, null);
          if (!resolved) {
            this.loading = false;
            this.loadError = `No published DVM dashboard found for "${this.slug}".`;
            return;
          }
          if (layoutJson) writeDynamicDashboardCache(this.slug, layoutJson);
          this.renderLayout(resolved);
        }),
      );
    });
  }

  private renderLayout(layoutJson: Record<string, unknown>): void {
    const generation = ++this.recomputeGeneration;
    this.renderVersion += 1;
    this.originalLayoutJson = layoutJson;
    this.dashboardName = String(layoutJson['dashboard-name'] ?? layoutJson['name'] ?? this.slug);
    // Surface the dashboard name in the shared top toolbar (beside the company
    // logo) instead of a tall in-page title block — reclaims the vertical space
    // the old `.dvm-published-toolbar` title row used to take.
    //
    // In Margin Estimator mode the top toolbar's COMPANY text is already forced
    // to "Margin Estimator" (side-navbar), so also pushing the dashboard name
    // there would show two near-identical headings side by side. Skip the push
    // in that mode — the branded company text is the single heading — and make
    // sure any stale title from a previous (non-margin) view is cleared.
    if (this.marginEstimatorMode) {
      this.dashboardTitleService.clearDashboardTitle();
    } else {
      this.dashboardTitleService.setDashboardTitle(this.dashboardName, 'top');
    }
    const widgets = this.extractWidgets(layoutJson['pageContent']);
    // Client-side charts (bar-column) + non-charts are 'ready' immediately; only
    // server-computed charts start in 'loading' pending the on-open recompute.
    // Snapshot-only widgets (table-matrix, file-upload) are skipped by every
    // recompute path (see recomputeChartsOnOpen), so they must be 'ready' too —
    // otherwise nothing ever clears their overlay.
    // Group cards are pure CONTAINERS published with no payload — no recompute
    // path ever resolves them, and on a filtered open (URL filterKey/filterValue)
    // applyIncomingNavigationFilter bumps the generation so the on-open
    // recompute's "not in batch → ready" cleanup never runs either: the card's
    // spinner stuck forever. Its children are their own recs with own statuses.
    this.widgets = widgets.map((rec) => ({
      ...rec,
      status:
        this.isChartWidget(rec.widget) && !this.isClientSideWidget(rec.widget) && !this.isSnapshotOnlyWidget(rec.widget) && !this.isGroupCardWidget(rec.widget)
          ? 'loading'
          : 'ready',
    }));
    // Snapshot client-side widgets' rows so cross-filter can be reversed.
    this.captureCrossFilterOriginals();
    // The layout comes straight from what the server returned (each widget's own
    // `layout.colSpan/rowSpan`, set by a previous saveLayout() publish) — NOT
    // localStorage, so every device/browser sees the same saved arrangement.
    this.layout = this.buildLayoutFromJson(layoutJson, this.widgets);
    this.loading = false;
    this.syncToolbarState();
    void this.maybeRecomputeChartsOnOpen(generation);
    // Apply any incoming table→dashboard navigation filter to the target charts.
    this.applyIncomingNavigationFilter();
  }

  /** Source-dashboard side: a table tile fired navigation → route to the target
   *  published dashboard, carrying the clicked value as query params so the
   *  target filters its charts on load. */
  onTableNavigate(event: { targetDashboard: string; targetComponents: string[]; filterKey: string; filterValue: string }): void {
    if (!event?.targetDashboard) return;
    this.router.navigate(['/dvm-published', event.targetDashboard], {
      queryParams: {
        filterKey: event.filterKey,
        filterValue: event.filterValue,
        ...(event.targetComponents?.length ? { filterTargets: event.targetComponents.join(',') } : {}),
      },
    });
  }

  /** A Normal/Editable table requested a refresh — re-fetch its dataset rows and
   *  swap them into the widget so the table re-renders with fresh data. */
  onTableRefresh(rec: PublishedWidgetRec): void {
    const w: any = rec?.widget || {};
    const caps = (w.capabilities || {}) as Record<string, unknown>;
    const dataset = String(caps['dataset'] || w?.payload?.dataset || w?.dataset || '').trim();
    if (!dataset) return;
    const url = buildSelfServiceApiUrl(`/${encodeURIComponent(dataset)}/dynamic_filter`);
    this.subs.add(
      this.http.post<unknown>(url, { uuid: null }).subscribe({
        next: (res) => {
          const rows = ((res as { data?: unknown })?.data ?? res) as Array<Record<string, unknown>>;
          if (!Array.isArray(rows)) return;
          this.widgets = this.widgets.map((r) => r.id === rec.id
            ? { ...r, widget: { ...r.widget, staticData: rows } }
            : r);
        },
        error: () => { /* keep current rows */ },
      }),
    );
  }

  /** Editable table Save All → persist the edited rows through the platform
   *  `dashboard_layouts/dynamic_filter` API. If the tile explicitly configures a
   *  custom save endpoint that wins; otherwise Save all posts the edited values
   *  to dashboard_layouts/dynamic_filter (the standard persistence API). */
  onTableSave(rec: PublishedWidgetRec, event: TableMatrixSaveEvent): void {
    const customUrl = (event?.saveUrl || '').trim();
    const target = customUrl
      ? buildSelfServiceApiUrl(customUrl.startsWith('/') ? customUrl : `/${customUrl}`)
      : buildSelfServiceApiUrl('/dashboard_layouts/dynamic_filter');
    // Resolve the current user's email (same helper publish/save use), then POST
    // an envelope carrying the edited rows (rowId + state + values) so the backend
    // can apply the create/update/delete for each changed record.
    this.resolveCurrentEmail().then((email) => {
      const payload = {
        email_id: [email],
        dashboard_id: this.dashboardName || this.slug,
        componentId: event.componentId,
        dataset: event.dataset,
        operation: event.operation,
        transactional: event.transactional === true,
        rows: event.rows,
      };
      this.subs.add(
        this.http.post<unknown>(target, payload).subscribe({
          next: () => { this.ui.showSuccess('Changes saved.'); this.onTableRefresh(rec); },
          error: () => this.ui.showError('Save failed — please try again.'),
        }),
      );
    });
  }

  /** Editable table workflow action. The component POSTs to a configured
   *  workflowUrl itself; this parent handler is the emit fallback (no URL set)
   *  so the action is surfaced rather than silently dropped. */
  onTableWorkflow(_rec: PublishedWidgetRec, event: TableMatrixWorkflowEvent): void {
    if (event?.workflowUrl) return; // component already POSTed
    console.info('[dvm-table] workflow action (no endpoint configured):', event);
    this.ui.showSuccess(`Workflow "${event.action}" on ${event.rowIds.length} row(s) — configure a Workflow endpoint to submit.`);
  }

  /** A `button`/`hyperlink` cell control was actioned in an editable table. */
  onTableCellAction(_rec: PublishedWidgetRec, event: TableMatrixCellActionEvent): void {
    console.info('[dvm-table] cell action:', event);
  }

  /** Target-dashboard side: if the URL carried a navigation filter, push it into
   *  the target components' compute payloads and recompute them — reusing the
   *  same machinery an input control (slider/dropdown) uses to drive targets.
   *  Empty target list = ALL chart widgets on this dashboard. */
  private applyIncomingNavigationFilter(): void {
    const nav = this.incomingNavFilter;
    if (!nav) return;
    // Which widgets to filter: the named targets, or every chart widget.
    const targetSet = new Set(nav.targetComponents);
    const targetRecs = this.widgets.filter((rec) => {
      if (!this.isChartWidget(rec.widget) || this.isTableMatrixWidget(rec.widget) || !rec.widget?.payload) return false;
      if (!targetSet.size) return true;
      return targetSet.has(String(rec.widget?.componentId ?? '').trim());
    });
    if (!targetRecs.length) return;

    const targetIds = targetRecs.map((rec) => String(rec.widget?.componentId ?? '').trim()).filter(Boolean);
    this.recomputingComponentIds = new Set(targetIds);
    // Bumping the generation below aborts maybeRecomputeChartsOnOpen — including
    // its "loading but not in the batch → ready" cleanup. Run the equivalent
    // cleanup here, or any widget renderLayout marked 'loading' that has no
    // payload (so it can't be a nav target) keeps its spinner forever.
    const targetRecIds = new Set(targetRecs.map((rec) => rec.id));
    this.widgets = this.widgets.map((rec) => {
      if (targetRecIds.has(rec.id)) return { ...rec, status: 'loading' };
      return rec.status === 'loading' ? { ...rec, status: 'ready' } : rec;
    });
    const generation = ++this.recomputeGeneration;

    // Each target recomputes with the filter injected into ITS OWN payload — the
    // backend receives the extra filterKey/filterValue (+ filters[]) alongside
    // the chart's normal compute payload and returns the scoped result.
    const requests = targetRecs.map((rec) => {
      const payload = {
        ...(rec.widget.payload ?? {}),
        filterKey: nav.filterKey,
        filterValue: nav.filterValue,
        filters: [{ column: nav.filterKey, value: nav.filterValue }],
      };
      rec.widget.payload = payload;
      return this.http.post<Record<string, unknown>>(this.computeUrlForWidget(rec.widget), payload).pipe(
        map((response) => ({ id: rec.id, widget: rec.widget, response, error: null as unknown })),
        catchError((error) => of({ id: rec.id, widget: rec.widget, response: null as Record<string, unknown> | null, error })),
      );
    });

    this.subs.add(
      forkJoin(requests).pipe(
        finalize(() => { if (generation === this.recomputeGeneration) this.recomputingComponentIds = new Set(); }),
      ).subscribe((results) => {
        if (generation !== this.recomputeGeneration) return;
        results.forEach(({ id, widget, response, error }) => {
          if (response && this.applyComputeResultToWidget(id, widget, response)) {
            this.setWidgetStatus(id, 'ready');
            return;
          }
          this.setWidgetStatus(id, error ? 'error' : 'ready', error ? 'Navigation filter compute failed; showing published data.' : undefined);
        });
      }),
    );
  }

  /** `publishedView.computeOnOpen` (dvm-layout-builder.json) gates whether
   *  charts re-run their compute payload on every open, or just show the
   *  published snapshot as-is. Cached after first read (same setting for the
   *  whole session). Defaults to true (the historical always-recompute
   *  behavior) only if the config is unreachable / doesn't say otherwise. */
  private computeOnOpenCache: boolean | null = null;
  private async shouldRecomputeOnOpen(): Promise<boolean> {
    if (this.computeOnOpenCache !== null) return this.computeOnOpenCache;
    try {
      const config = await firstValueFrom(this.http.get<Record<string, unknown>>('/assets/self-service-config/dvm-layout-builder.json'));
      const publishedView = config?.['publishedView'] as Record<string, unknown> | undefined;
      this.computeOnOpenCache = publishedView?.['computeOnOpen'] !== false;
    } catch {
      this.computeOnOpenCache = true;
    }
    return this.computeOnOpenCache;
  }

  private async maybeRecomputeChartsOnOpen(generation: number): Promise<void> {
    const shouldRecompute = await this.shouldRecomputeOnOpen();
    if (generation !== this.recomputeGeneration) return; // a newer load superseded this one
    if (!shouldRecompute) {
      // Snapshot from dynamic_filter is the source of truth — no recompute will
      // run, so flip every tile renderLayout marked 'loading' to 'ready' here,
      // otherwise their busy overlays never clear.
      this.widgets = this.widgets.map((rec) => (rec.status === 'loading' ? { ...rec, status: 'ready' } : rec));
      return;
    }
    // Same group-card exclusion as renderLayout: containers are never computed,
    // so they must never re-enter 'loading' here either.
    this.widgets = this.widgets.map((rec) => (this.isChartWidget(rec.widget) && !this.isSnapshotOnlyWidget(rec.widget) && !this.isGroupCardWidget(rec.widget) ? { ...rec, status: 'loading' } : rec));
    this.recomputeChartsOnOpen();
  }

  private extractWidgets(pageContent: unknown): Array<{ id: string; widget: any }> {
    const out: Array<{ id: string; widget: any }> = [];
    const count = new Map<string, number>();
    const register = (node: any): { id: string; widget: any } => {
      const base = String(node.widgetId ?? node.id ?? `widget-${out.length + 1}`).replace(/[^a-zA-Z0-9-_]/g, '-');
      const idx = (count.get(base) ?? 0) + 1;
      count.set(base, idx);
      const id = idx === 1 ? base : `${base}__${idx}`;
      const widget = this.normalizePublishedWidget({ ...node });
      if (this.isInputWidget(widget)) {
        widget.onWidgetValueChange = (value: unknown) => this.onInputWidgetChange(widget, value);
      }
      const entry = { id, widget };
      out.push(entry);
      return entry;
    };
    const visit = (node: any): void => {
      if (!node || typeof node !== 'object') return;
      if (node.type) {
        const entry = register(node);
        // A 'group-card' widget's children flatten into the SAME list `out` — as
        // their own independent PublishedWidgetRec entries — so every existing
        // compute/recompute/target-driving path (all keyed by componentId over
        // a flat `this.widgets`) works on a grouped chart exactly like a
        // top-level one. They never get a `this.layout` grid entry of their own
        // (buildLayoutFromJson only reads the outer layout.items snapshot, one
        // per TOP-LEVEL tile), so viewItems naturally never renders them a
        // second time — only the group card's own nested loop does, via
        // `childWidgetIds` + widgetRecById().
        if (String(node.type) === 'group-card' && Array.isArray(node.children)) {
          entry.widget.childWidgetIds = (node.children as any[])
            .filter((child) => child && typeof child === 'object')
            .map((child) => register(child).id);
        }
        return;
      }
      if (Array.isArray(node.columns)) node.columns.forEach((child: any) => visit(child));
      if (Array.isArray(node.rows)) node.rows.forEach((child: any) => visit(child));
    };
    if (Array.isArray(pageContent)) pageContent.forEach((row) => visit(row));
    return out;
  }

  /** Looks up a (possibly nested, group-card child) widget record by id — used
   *  by the template to render a group's members via the same widgetBody
   *  ng-template the top-level tiles use. */
  widgetRecById(id: string): PublishedWidgetRec | undefined {
    return this.widgets.find((w) => w.id === id);
  }

  /** Group members that are input CONTROLS (slider / input box / dropdown /
   *  file upload) render compact — content height only, no 160px chart floor —
   *  so a group of inputs fits its card without the members list scrolling. */
  isCompactGroupMember(widget: any): boolean {
    const type = String(widget?.type ?? '').toLowerCase();
    return type === 'slider' || type === 'input-box' || type === 'dropdown' || type === 'file-upload';
  }

  private normalizePublishedWidget(widget: any): any {
    const formattedWidget = this.withApiFormatFields(widget);
    const normType = String(formattedWidget?.type ?? '').toLowerCase();
    if (formattedWidget && (normType === 'bar-column' || normType === 'line-area' || normType === 'scatter-bubble')) {
      // The builder persists a fixed pixel height (rowSpan × 40px) but the
      // published grid sizes tiles at rowSpan × 48px plus row gaps — the chart
      // always came up short, leaving a dead band under it. The tile (with its
      // card header) is the size authority here, so let the chart fill it.
      return { ...formattedWidget, height: '100%' };
    }
    if (!formattedWidget || String(formattedWidget?.type ?? '').toLowerCase() !== 'waterfall-chart') return formattedWidget;
    const bars = Array.isArray(formattedWidget.waterfallBars) ? formattedWidget.waterfallBars : null;
    // The template's waterfall-card-header is the ONE header — always suppress
    // the component's internal card-header, even for a data-less widget (a
    // corrupted/legacy one otherwise rendered BOTH: the double-title bug).
    if (!bars && (!formattedWidget.option || typeof formattedWidget.option !== 'object')) {
      return { ...formattedWidget, hideCardHeader: true };
    }

    const format = formattedWidget.format as Record<string, unknown> | undefined;
    const sourceOption = (bars ? buildWaterfallOption(bars, format) : formattedWidget.option) as EChartsOption;
    const option = { ...(sourceOption as Record<string, unknown>) };
    delete option['title'];

    return {
      ...formattedWidget,
      hideCardHeader: true,
      option: {
        ...option,
        grid: this.withWaterfallTitleGridTop(sourceOption.grid),
        xAxis: this.withWaterfallXAxisLabelRotation(sourceOption.xAxis, format),
      },
    };
  }

  private withApiFormatFields(widget: any): any {
    const format = widget?.format as Record<string, unknown> | undefined;
    if (!format || typeof format !== 'object') return widget;

    const type = String(widget?.type ?? '').toLowerCase();
    const next = { ...widget };
    const formattedTitle = String(format['title'] ?? '').trim();
    if (formattedTitle) next.title = formattedTitle;

    if (type === 'kpi-card' || type === 'horizontal-bar-chart') {
      next.numberFormat = this.toKpiNumberFormat(format);
      next.numberScale = this.toNumberScale(format['numberFormat']);
      next.decimals = Number(format['decimals'] ?? next.decimals ?? 1);
    }

    if (type === 'kpi-card') {
      next.prefix = String(format['prefix'] ?? '');
      next.suffix = String(format['suffix'] ?? '');
    }

    if (type === 'horizontal-bar-chart') {
      const positiveColor = String(format['positiveColor'] ?? '').trim();
      if (positiveColor) {
        next.color = positiveColor;
        next.barColor = positiveColor;
      }
      next.showValues = format['showValues'] !== false;
      next.valueLabelPosition = format['showValues'] === false ? 'none' : (next.valueLabelPosition || 'inside');
    }

    return next;
  }

  private toKpiNumberFormat(format?: Record<string, unknown>): string {
    const selected = String(format?.['numberFormat'] ?? 'NONE');
    if (selected === 'THOUSANDS') return 'thousands';
    if (selected === 'PERCENT') return 'percent';
    return 'plain';
  }

  private toNumberScale(value: unknown): string {
    switch (String(value ?? 'NONE')) {
      case 'COMPACT': return 'compact';
      case 'SCALE_THOUSANDS': return 'thousands';
      case 'MILLIONS': return 'millions';
      case 'BILLIONS': return 'billions';
      default: return 'none';
    }
  }

  private withWaterfallTitleGridTop(grid: EChartsOption['grid']): EChartsOption['grid'] {
    const addTop = (item: unknown): unknown => ({ ...(item as Record<string, unknown> | undefined), top: Math.max(Number((item as any)?.top ?? 32), 32) });
    return Array.isArray(grid) ? grid.map(addTop) as EChartsOption['grid'] : addTop(grid) as EChartsOption['grid'];
  }

  private withWaterfallXAxisLabelRotation(xAxis: EChartsOption['xAxis'], format?: Record<string, unknown>): EChartsOption['xAxis'] {
    const rotate = this.toAxisLabelRotation(format?.['xAxisLabelRotation'], 30);
    const patchAxis = (axis: unknown): unknown => ({
      ...((axis as Record<string, unknown> | undefined) || {}),
      axisLabel: {
        ...(((axis as Record<string, unknown> | undefined)?.['axisLabel'] as Record<string, unknown> | undefined) || {}),
        rotate,
      },
    });
    return Array.isArray(xAxis) ? xAxis.map(patchAxis) as EChartsOption['xAxis'] : patchAxis(xAxis) as EChartsOption['xAxis'];
  }

  private toAxisLabelRotation(value: unknown, fallback: number): number {
    const rotation = Number(value ?? fallback);
    return Number.isFinite(rotation) ? rotation : fallback;
  }
  private recomputeChartsOnOpen(): void {
    const generation = this.recomputeGeneration;
    const chartRecords = this.widgets.filter(
      (rec) => this.isChartWidget(rec.widget) && !this.isClientSideWidget(rec.widget) && !this.isSnapshotOnlyWidget(rec.widget) && rec.widget?.payload,
    );
    // Anything marked 'loading' that ISN'T in the recompute batch (e.g. a chart
    // widget published without a payload) would keep its overlay forever —
    // flip those to 'ready' now; they render their snapshot as-is.
    const batchIds = new Set(chartRecords.map((rec) => rec.id));
    this.widgets = this.widgets.map((rec) => (rec.status === 'loading' && !batchIds.has(rec.id) ? { ...rec, status: 'ready' } : rec));
    if (!chartRecords.length) {
      this.reapplyActiveInputOverrides(generation);
      return;
    }

    // The drift diff below serialises a whole widget (staticData rows included)
    // TWICE per chart. It only feeds scheduleAutoPersistOverride, which no-ops
    // for anyone who can't persist — so for those viewers skip it entirely
    // rather than paying it on every dashboard open.
    const canPersistDrift = this.isOwner && !this.isSharedReadonlyView;

    // Apply each chart AS IT LANDS. forkJoin held every tile blank until the
    // SLOWEST request finished, which is why a dashboard felt slow to load even
    // though each individual compute returned quickly.
    let pending = chartRecords.length;
    let changed = false;
    const finish = () => {
      if (--pending > 0) return;
      if (changed) this.scheduleAutoPersistOverride();
      // Base KPIs/charts above just refreshed straight from the dataset, with
      // no knowledge of any slider/input the user had previously dialed in —
      // that would silently reset an active override back to its plain
      // dataset value (the exact regression a `computeOnOpen` comment above
      // already warns about). Replay each active input's own compute call
      // AFTER the fresh base is in place, so its formula re-applies on top of
      // the (possibly changed) current data instead of being lost.
      this.reapplyActiveInputOverrides(generation);
    };

    chartRecords.forEach((rec) => {
      this.subs.add(
        this.http.post<Record<string, unknown>>(this.computeUrlForWidget(rec.widget), rec.widget.payload).pipe(
          map((response) => ({ response, error: null as unknown })),
          catchError((error) => of({ response: null, error })),
        ).subscribe(({ response, error }) => {
          if (generation !== this.recomputeGeneration) return;
          if (response) {
            // Detect whether the LIVE dataset produced different values than the
            // published snapshot — if the source data was edited upstream since
            // this dashboard was last saved, this pass is what surfaces it, and
            // the refreshed values get persisted so the snapshot stops drifting.
            const before = canPersistDrift
              ? JSON.stringify(this.widgets.find((w) => w.id === rec.id)?.widget ?? null)
              : '';
            const applied = this.applyComputeResultToWidget(rec.id, rec.widget, response);
            if (applied) {
              if (canPersistDrift) {
                const after = JSON.stringify(this.widgets.find((w) => w.id === rec.id)?.widget ?? null);
                if (after !== before) changed = true;
              }
              this.setWidgetStatus(rec.id, 'ready');
              finish();
              return;
            }
          }
          this.setWidgetStatus(rec.id, error ? 'error' : 'ready', error ? 'Fresh compute failed; showing published snapshot.' : undefined);
          finish();
        }),
      );
    });
  }

  /** Slider/input widgets whose value has actually been moved away from its
   *  default carry an intentional user override — replay their compute call
   *  (same POST `onInputWidgetChange` makes) so the override's formula lands
   *  on top of whatever the base dataset refresh just produced, then persist
   *  the combined result. Widgets left at their default aren't "overridden",
   *  so there's nothing to replay for them. */
  private reapplyActiveInputOverrides(generation: number): void {
    const inputRecs = this.widgets.filter((rec) => this.isInputWidget(rec.widget) && rec.widget?.payload);
    const active = inputRecs.filter((rec) => {
      const value = Number(rec.widget.payload?.value);
      const defaultValue = Number(rec.widget.payload?.defaultValue ?? 0);
      return Number.isFinite(value) && value !== defaultValue;
    });
    if (!active.length) return;

    const requests = active.map((rec) => {
      const payload = { ...rec.widget.payload, otherInputs: this.otherActiveInputValues(String(rec.widget?.componentId ?? '')) };
      return this.http.post<Record<string, unknown>>(this.computeUrlForWidget(rec.widget), payload).pipe(
        map((response) => ({ rec, response, error: null as unknown })),
        catchError((error) => of({ rec, response: null, error })),
      );
    });

    this.subs.add(
      forkJoin(requests).subscribe((results) => {
        if (generation !== this.recomputeGeneration) return;
        results.forEach(({ rec, response, error }) => {
          if (response) {
            this.applyRecomputeResults(response, this.extractTargetIds(rec.widget.payload?.targets));
          } else if (error) {
            console.warn('[dvm-published] reapplying input override for', rec.widget?.componentId, 'failed:', error);
          }
        });
        this.scheduleAutoPersistOverride();
      }),
    );
  }

  /** Every OTHER input control currently on the dashboard, with its live value —
   *  bundled into every single-input recompute call so a target/KPI formula
   *  referencing another input's [Token] (see `references` in the design-time
   *  reference rows) resolves against what that input is showing RIGHT NOW,
   *  not a value the backend may have cached from an earlier call. Keyed by
   *  componentId (unambiguous) and title (matches the reference metadata's
   *  typed `inputTitle`, so the backend can match either way). */
  private otherActiveInputValues(excludeComponentId: string): Array<{ componentId: string; title: string; value: unknown }> {
    return this.widgets
      .filter((rec) => this.isInputWidget(rec.widget) && String(rec.widget?.componentId ?? '') !== String(excludeComponentId))
      .map((rec) => ({
        componentId: String(rec.widget?.componentId ?? ''),
        title: String(rec.widget?.title ?? ''),
        value: rec.widget?.payload?.value ?? rec.widget?.payload?.defaultValue ?? null,
      }))
      .filter((entry) => !!entry.componentId);
  }

  /** SSA $ resolves Price Inflation %/Cost Inflation %'s own cross-referenced
   *  values (DN Price, Total Cost, ...) from whatever the backend last cached
   *  for THEM - but every slider's compute call is independent, so moving SSA
   *  $ alone never refreshes that cache unless Price Inflation %/Cost
   *  Inflation % have ALSO been (re-)submitted since. Replaying their own
   *  compute call first - with whatever value they currently show, even their
   *  default/zero - keeps every cross-reference SSA $ depends on fresh,
   *  matching what the Waterfall itself would show if those sliders were
   *  touched directly. */
  private sideEffectInputsFor(widget: any): Array<{ widget: any }> {
    const title = String(widget?.title ?? '').trim().toLowerCase();
    if (title !== 'ssa $') return [];
    const wanted = new Set(['price inflation %', 'cost inflation %']);
    return this.widgets
      .filter((rec) => this.isInputWidget(rec.widget)
        && String(rec.widget?.componentId ?? '') !== String(widget?.componentId ?? '')
        && wanted.has(String(rec.widget?.title ?? '').trim().toLowerCase()))
      .map((rec) => ({ widget: rec.widget }));
  }

  onInputWidgetChange(widget: any, rawValue: unknown): void {
    const value = rawValue && typeof rawValue === 'object' && 'end' in (rawValue as Record<string, unknown>)
      ? (rawValue as Record<string, unknown>)['end']
      : rawValue;
    const payload = { ...(widget?.payload ?? {}), value };
    payload['otherInputs'] = this.otherActiveInputValues(String(widget?.componentId ?? ''));

    // A dropdown acts as a GLOBAL filter — its change refreshes EVERY chart on
    // the dashboard, not only a pre-selected target. Rebuild its targets from
    // all current chart widgets so the compute call returns a result per chart.
    // (Sliders/input boxes keep their own explicit targets.)
    if (String(widget?.type ?? '').toLowerCase() === 'dropdown') {
      // The client-side family (bar-column / line-area / scatter-bubble) has no
      // server recompute (buildRecomputedWidgetFields returns null for them) —
      // filter their staticData locally from the captured originals instead,
      // composing across several dropdowns.
      const dropdownField = String(payload['field'] ?? '');
      const dropdownValues = Array.isArray(value) ? value.map(String) : value == null || value === '' ? [] : [String(value)];
      if (dropdownField && dropdownValues.length) {
        this.activeDropdownFilters.set(String(widget?.componentId ?? ''), { field: dropdownField, values: dropdownValues });
      } else {
        this.activeDropdownFilters.delete(String(widget?.componentId ?? ''));
      }
      this.applyDropdownFiltersToClientWidgets();
      const rebuilt = this.widgets
        .filter((rec) => this.isChartWidget(rec.widget) && String(rec.widget?.componentId ?? '') !== String(widget?.componentId ?? ''))
        .map((rec) => ({ componentId: String(rec.widget?.componentId ?? ''), label: rec.widget?.title ?? '', field: payload['field'] ?? '' }))
        .filter((target) => !!target.componentId);
      // Only overwrite when we actually resolved chart targets. If the rebuild
      // comes back empty (e.g. chart componentIds not where expected), keep the
      // targets that were PERSISTED with the dropdown at publish — otherwise
      // we'd wipe them and the `!targetIds.length` guard below would skip the
      // compute call entirely (the "dropdown compute not firing" symptom).
      if (rebuilt.length) {
        payload['targets'] = rebuilt;
      } else if (!Array.isArray(payload['targets'])) {
        payload['targets'] = [];
      }
    }
    widget.payload = payload;

    const targetIds = this.extractTargetIds(payload['targets']);
    if (!targetIds.length) {
      this.recomputeGeneration += 1;
      this.recomputingComponentIds = new Set();
      return;
    }

    const sideEffectInputs = this.sideEffectInputsFor(widget);
    this.recomputingComponentIds = new Set([...targetIds, ...sideEffectInputs.map((s) => String(s.widget?.componentId ?? ''))]);
    const generation = ++this.recomputeGeneration;

    const runMainCompute = () => this.subs.add(
      this.http.post<Record<string, unknown>>(this.computeUrlForWidget(widget), payload).pipe(
        finalize(() => { if (generation === this.recomputeGeneration) this.recomputingComponentIds = new Set(); }),
      ).subscribe({
        next: (response) => {
          if (generation !== this.recomputeGeneration) return;
          this.applyRecomputeResults(response, targetIds);
          // The slider/input value itself is a direct user edit — always
          // persist it (unlike the on-open dataset refresh, there's no need to
          // diff first; every call here is a real, intentional override).
          this.scheduleAutoPersistOverride();
        },
        error: (error) => console.warn('[dvm-published] input recompute failed:', error),
      }),
    );

    if (!sideEffectInputs.length) {
      runMainCompute();
      return;
    }

    // Replay Price Inflation %/Cost Inflation %'s own compute call FIRST -
    // with whatever value they currently show, even default/zero - so their
    // cross-referenced values are freshly cached before SSA $'s own call
    // resolves against them.
    const sideEffectRequests = sideEffectInputs.map(({ widget: sideWidget }) => {
      const sidePayload = { ...(sideWidget?.payload ?? {}), otherInputs: this.otherActiveInputValues(String(sideWidget?.componentId ?? '')) };
      return this.http.post<Record<string, unknown>>(this.computeUrlForWidget(sideWidget), sidePayload).pipe(
        map((response) => ({ sideWidget, response, error: null as unknown })),
        catchError((error) => of({ sideWidget, response: null, error })),
      );
    });

    this.subs.add(
      forkJoin(sideEffectRequests).subscribe((results) => {
        if (generation !== this.recomputeGeneration) return;
        results.forEach(({ sideWidget, response, error }) => {
          if (response) {
            this.applyRecomputeResults(response, this.extractTargetIds(sideWidget?.payload?.targets));
          } else if (error) {
            console.warn('[dvm-published] SSA $ side-effect recompute for', sideWidget?.componentId, 'failed:', error);
          }
        });
        runMainCompute();
      }),
    );
  }

  // ── File upload widget (published) — POST the file, re-render targets ──────
  private uploadingWidgetIds = new Set<string>();
  private fileUploadMessages: Record<string, string> = {};
  isFileUploading(widget: any): boolean { return this.uploadingWidgetIds.has(String(widget?.componentId ?? widget?.widgetId ?? '')); }
  fileUploadMessage(widget: any): string { return this.fileUploadMessages[String(widget?.componentId ?? widget?.widgetId ?? '')] || ''; }

  /** A file was picked on a published file-upload widget → base64-encode it into
   *  a JSON payload and POST to the compute API (JSON-only — multipart gives 415)
   *  tagged with the widget's targets, then apply the per-target results (reuses
   *  the input-widget recompute machinery). */
  onFilePicked(widget: any, file: File): void {
    if (!file) return;
    const key = String(widget?.componentId ?? widget?.widgetId ?? '');
    const targetIds = this.extractTargetIds(widget?.payload?.targets ?? widget?.targets);

    this.uploadingWidgetIds = new Set(this.uploadingWidgetIds).add(key);
    this.fileUploadMessages = { ...this.fileUploadMessages, [key]: `Uploading ${file.name}…` };
    if (targetIds.length) this.recomputingComponentIds = new Set(targetIds);
    const generation = ++this.recomputeGeneration;

    this.fileToBase64(file).then((base64) => {
      const payload: Record<string, unknown> = {
        ...(widget?.payload ?? {}),
        componentId: String(widget?.componentId ?? ''),
        componentType: 'INPUT',
        inputType: 'FILE_UPLOAD',
        fileName: file.name,
        fileType: file.type,
        fileContent: base64,
        // The compute API requires every INPUT to carry a `value` (same field
        // dropdowns/sliders send); a file-upload's value IS the file.
        value: base64,
        defaultValue: base64,
        targets: targetIds.map((componentId) => ({ componentId })),
      };
      this.subs.add(
        this.http.post<Record<string, unknown>>(this.computeUrlForWidget(widget), payload).pipe(
          finalize(() => {
            this.uploadingWidgetIds.delete(key);
            this.uploadingWidgetIds = new Set(this.uploadingWidgetIds);
            if (generation === this.recomputeGeneration) this.recomputingComponentIds = new Set();
          }),
        ).subscribe({
          next: (response) => {
            if (generation === this.recomputeGeneration && targetIds.length) this.applyRecomputeResults(response, targetIds);
            this.fileUploadMessages = { ...this.fileUploadMessages, [key]: `Uploaded ${file.name}` };
          },
          error: () => {
            this.fileUploadMessages = { ...this.fileUploadMessages, [key]: `Upload failed for ${file.name}` };
          },
        }),
      );
    }).catch(() => {
      this.uploadingWidgetIds.delete(key);
      this.uploadingWidgetIds = new Set(this.uploadingWidgetIds);
      this.recomputingComponentIds = new Set();
      this.fileUploadMessages = { ...this.fileUploadMessages, [key]: `Could not read ${file.name}` };
    });
  }

  /** Read a File as a base64 string (no data-URL prefix — just the base64). */
  private fileToBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = String(reader.result || '');
        const comma = result.indexOf(',');
        resolve(comma >= 0 ? result.slice(comma + 1) : result);
      };
      reader.onerror = () => reject(reader.error);
      reader.readAsDataURL(file);
    });
  }

  private extractTargetIds(rawTargets: unknown): string[] {
    if (!Array.isArray(rawTargets)) return [];
    return rawTargets
      .map((target) => {
        const rec = target as Record<string, unknown>;
        return String(rec?.['componentId'] ?? '');
      })
      .map((id) => id.trim())
      .filter(Boolean);
  }


  private applyRecomputeResults(response: Record<string, unknown> | null, targetIds: string[]): void {
    if (!targetIds.length) return;
    const results = (response?.['results'] ?? []) as Array<Record<string, unknown>>;
    if (!Array.isArray(results) || !results.length) return;
    const allowedIds = new Set(targetIds);
    results.forEach((entry) => {
      if (!recomputeEntryHasData(entry)) return;
      const result = (entry?.['result'] ?? entry) as Record<string, unknown>;
      const componentId = String(entry?.['componentId'] ?? result?.['componentId'] ?? '').trim();
      const rec = this.findRecomputeTargetWidget(componentId, resolveRecomputeChartType(entry), allowedIds);
      if (!rec) return;
      if (this.isInputWidget(rec.widget)) {
        this.applyValueToInputWidget(rec.id, rec.widget, entry);
      } else {
        this.applyComputeResultToWidget(rec.id, rec.widget, entry);
      }
      this.setWidgetStatus(rec.id, 'ready');
    });
  }

  private findRecomputeTargetWidget(componentId: string, chartType: string, allowedIds: Set<string>): PublishedWidgetRec | undefined {
    if (componentId && allowedIds.has(componentId)) {
      const direct = this.widgets.find((item) => String(item.widget?.componentId ?? '').trim() === componentId);
      if (direct) return direct;
    }

    if (!chartType) return undefined;
    // Type fallback for when the backend didn't echo a componentId — BUT only
    // when it's unambiguous. Previously this returned the FIRST allowed widget
    // of the matching type, so a dropdown/input driving two same-type charts
    // (or pointing at a stale id after a target was regenerated) silently drove
    // the WRONG chart. Only fall back when exactly ONE allowed target has this
    // type; otherwise refuse to guess.
    const sameType = this.widgets.filter((item) => {
      const id = String(item.widget?.componentId ?? '').trim();
      return allowedIds.has(id) && this.layoutTypeForWidget(item.widget) === chartType;
    });
    return sameType.length === 1 ? sameType[0] : undefined;
  }

  /** A target that is itself an input control just needs its live `value`
   *  pushed in — the backend already computed it from the driving control's
   *  formula. Unlike a chart, that has to land in TWO places: `payload.value`
   *  (so this control can, in turn, drive its OWN targets with the fresh
   *  value) and whatever field the actual rendered slider/input-box widget
   *  reads its displayed value from. */
  private applyValueToInputWidget(id: string, widget: any, entry: Record<string, unknown>): void {
    const fields = buildRecomputedWidgetFields(entry, { widgetId: String(widget?.widgetId ?? id) });
    const value = fields?.['value'];
    if (value === undefined) return;
    const isSlider = String(widget?.type ?? '').toLowerCase() === 'slider';
    const nextWidget = {
      ...widget,
      payload: { ...(widget?.payload ?? {}), value },
      ...(isSlider
        ? { data: { ...(widget?.data ?? {}), data: { ...(widget?.data?.data ?? {}), rangeStart: value, rangeEnd: value } } }
        : { value }),
    };
    this.widgets = this.widgets.map((rec) => (rec.id === id ? { ...rec, widget: nextWidget } : rec));
  }
  private applyComputeResultToWidget(id: string, widget: any, responseOrEntry: Record<string, unknown>): boolean {
    // Table-matrix targets re-render from raw rows (or a drill tree) — the
    // shared buildRecomputedWidgetFields has no table-matrix case, so handle it
    // here: swap the response rows into the widget's staticData (or drilldownData
    // for a drill-down table), preserving its capabilities.
    if (this.isTableMatrixWidget(widget)) {
      const entry = (responseOrEntry?.['result'] ?? responseOrEntry) as Record<string, unknown>;
      const rows = this.extractTableRows(entry);
      const caps = (widget?.capabilities ?? {}) as Record<string, unknown>;
      const isDrill = caps['tableType'] === 'drilldown' || (caps['typeFlags'] as Record<string, unknown> | undefined)?.['drilldown'] === true;
      const patch = isDrill ? { drilldownData: entry } : { staticData: rows };
      this.widgets = this.widgets.map((rec) => rec.id === id
        ? { ...rec, widget: this.normalizePublishedWidget({ ...rec.widget, ...patch }) }
        : rec);
      return true;
    }
    const normalizedEntry = this.normalizeComputeEntry(widget, responseOrEntry);
    if (!recomputeEntryHasData(normalizedEntry)) return false;
    const fields = buildRecomputedWidgetFields(normalizedEntry, {
      widgetId: String(widget?.widgetId ?? id),
      title: widget?.title,
      yAxisName: widget?.yAxisName,
      xAxisName: widget?.xAxisName,
      kpiField: widget?.payload?.field,
      format: widget?.format,
      payload: widget?.payload,
      capabilities: widget?.capabilities,
    });
    if (!fields) return false;

    this.widgets = this.widgets.map((rec) => rec.id === id
      ? { ...rec, widget: this.normalizePublishedWidget({ ...rec.widget, ...fields, onWidgetValueChange: rec.widget?.onWidgetValueChange }) }
      : rec);
    return true;
  }

  private normalizeComputeEntry(widget: any, responseOrEntry: Record<string, unknown>): Record<string, unknown> {
    if (responseOrEntry['result']) return responseOrEntry;
    const chartType = this.layoutTypeForWidget(widget);
    return {
      componentId: widget?.componentId,
      result: {
        ...responseOrEntry,
        chartType: responseOrEntry['chartType'] ?? chartType,
        componentId: responseOrEntry['componentId'] ?? widget?.componentId,
      },
    };
  }

  private computeUrlForWidget(widget: any): string {
    return resolveSelfServiceApiUrl(widget?.recomputeUrl, '/components/compute');
  }

  private setWidgetStatus(id: string, status: PublishedWidgetRec['status'], error?: string): void {
    this.widgets = this.widgets.map((rec) => rec.id === id ? { ...rec, status, error } : rec);
  }

  private isInputWidget(widget: any): boolean {
    const type = String(widget?.type ?? '').toLowerCase();
    return type === 'slider' || type === 'input-box' || type === 'dropdown';
  }

  /** Edit mode forces CLASSIC dropdown tiles compact (span 2) — but the
   *  button/tile display modes render every option as a visible pill and need
   *  the tile's real persisted height. */
  isCompactDropdownTile(widget: any): boolean {
    if (String(widget?.type ?? '').toLowerCase() !== 'dropdown') return false;
    const mode = String(widget?.displayMode ?? 'dropdown');
    return mode !== 'buttons' && mode !== 'tiles';
  }

  private isChartWidget(widget: any): boolean {
    return !!widget?.type && !this.isInputWidget(widget);
  }

  /** Bar & Column tiles render CLIENT-SIDE from their published staticData +
   *  capabilities — there's no server compute for them, so they must be excluded
   *  from the on-open recompute (otherwise the POST fails and shows a spurious
   *  "Fresh compute failed" warning even though the chart is fine). */
  private isClientSideWidget(widget: any): boolean {
    const type = String(widget?.type);
    return type === 'bar-column' || type === 'line-area' || type === 'scatter-bubble';
  }

  // ── Cross-filter (published view) — mirrors the builder's generic engine ───

  /** True when a widget renders client-side and can publish/react to cross-filters. */
  private isCrossFilterWidget(widget: any): boolean {
    return DvmPublishedDashboardComponent.CROSS_FILTER_TYPES.has(String(widget?.type ?? '')) && Array.isArray(widget?.staticData);
  }

  /** Waterfall widgets react to cross-filters via SERVER recompute (aggregated
   *  bars, no client rows) — needs the persisted compute payload to re-run. */
  private isRecomputeCrossFilterWidget(widget: any): boolean {
    return String(widget?.type ?? '') === 'waterfall-chart' && Array.isArray(widget?.waterfallBars) && !!widget?.payload;
  }

  /** The column a widget publishes as its cross-filter key: its persisted
   *  `filterKey` override, else its category column from capabilities. */
  private publishedFilterKey(widget: any): string {
    const key = String(widget?.filterKey ?? '').trim();
    if (key) return key;
    return String(widget?.capabilities?.category ?? '');
  }

  /** The widget ids that REACT to a source widget: its persisted `listenerTileIds`
   *  when set, else every OTHER cross-filter-capable widget (client-side charts
   *  plus recompute listeners like the waterfall) as the default. */
  private publishedListenerIds(sourceRec: PublishedWidgetRec): string[] {
    const explicit = sourceRec.widget?.listenerTileIds;
    if (Array.isArray(explicit)) return explicit.map(String);
    return this.widgets
      .filter((r) => r.id !== sourceRec.id && (this.isCrossFilterWidget(r.widget) || this.isRecomputeCrossFilterWidget(r.widget)))
      .map((r) => r.id);
  }

  /** Handle a cross-filter click emitted by a published chart. Sets/clears the
   *  active filter (multi-select) and re-renders the reacting widgets. */
  onPublishedCrossFilter(sourceRec: PublishedWidgetRec, event: { column: string; value: string | null; values?: string[] }): void {
    const column = this.publishedFilterKey(sourceRec.widget) || event.column;
    const values = Array.isArray(event.values) ? event.values : (event.value ? [event.value] : []);
    this.activeCrossFilter = values.length ? { column, values, sourceId: sourceRec.id } : null;
    // Notify the backend which value was clicked and where the filter applied.
    // Filtering itself stays client-side (instant), so this call is best-effort:
    // errors never block the UI. Skipped when the filter is cleared (no values).
    this.postCrossFilterEvent(sourceRec, column, values);
    this.applyPublishedCrossFilter();
  }

  /** Best-effort POST to /{entity}/dynamic_filter describing the cross-filter
   *  click: the source chart, the column it publishes, and the clicked value(s),
   *  plus the widgets that react. Fire-and-forget — the UI never waits on it and
   *  a failure is swallowed (client-side filtering already happened). Config-free:
   *  the dataset comes from the source widget's own payload. */
  private postCrossFilterEvent(sourceRec: PublishedWidgetRec, column: string, values: string[]): void {
    if (!values.length) return; // filter cleared — nothing to report
    const w: any = sourceRec.widget || {};
    const multiDataset = Array.isArray(w?.payload?.datasets) ? String(w.payload.datasets[0] ?? '') : '';
    const dataset = String(w?.payload?.dataset ?? '').trim() || multiDataset.trim() || String(w?.dataset ?? '').trim();
    if (!dataset) return; // no dataset to scope the call to
    const url = buildSelfServiceApiUrl(`/${encodeURIComponent(dataset)}/dynamic_filter`);
    const body = {
      uuid: null,
      // The cross-filter selection: which column, which clicked value(s), which
      // source chart published it, and which tiles react. `filters` is the shape
      // a filter-aware backend would consume (column IN values).
      crossFilter: {
        column,
        values,
        sourceWidgetId: sourceRec.id,
        listenerWidgetIds: this.publishedListenerIds(sourceRec),
      },
      filters: [{ column, operator: 'IN', values }],
    };
    this.subs.add(
      this.http.post(url, body).pipe(catchError(() => of(null))).subscribe(),
    );
  }

  /** Re-render every reacting client-side widget from its ORIGINAL rows filtered
   *  by column IN values (or restore all rows when cleared). Generic across every
   *  registered cross-filter chart type; the source widget keeps its own data.
   *  RECOMPUTE listeners (waterfall) can't filter client-side — they re-run their
   *  compute payload with the filter attached (see recomputePublishedWaterfall). */
  private applyPublishedCrossFilter(): void {
    const filter = this.activeCrossFilter;
    const sourceRec = filter ? this.widgets.find((r) => r.id === filter.sourceId) : undefined;
    const listeners = sourceRec ? new Set(this.publishedListenerIds(sourceRec)) : new Set<string>();
    const filterValues = filter ? new Set(filter.values) : null;

    this.widgets = this.widgets.map((rec) => {
      if (!this.isCrossFilterWidget(rec.widget)) return rec;
      if (filter && rec.id === filter.sourceId) return rec;
      const original = this.crossFilterOriginalRows.get(rec.id) ?? [];
      const reacts = !!filter && listeners.has(rec.id) && original.some((row) => filter.column in row);
      const rows = reacts && filterValues
        ? original.filter((row) => filterValues.has(String(row[filter!.column] ?? '')))
        : original;
      // New widget object ref (and staticData ref) so the chart's ngOnChanges fires.
      return { ...rec, widget: { ...rec.widget, staticData: rows } };
    });

    // Server-recompute listeners (waterfall bridges): aggregated charts can only
    // react by recomputing on the filtered data. Fire per-widget recomputes for
    // listening waterfalls; restore any other/cleared waterfall to its cached
    // unfiltered bars (instant, no API call).
    this.widgets.forEach((rec) => {
      if (!this.isRecomputeCrossFilterWidget(rec.widget)) return;
      if (filter && rec.id === filter.sourceId) return;
      if (filter && listeners.has(rec.id)) this.recomputePublishedWaterfall(rec, filter);
      else this.restorePublishedWaterfall(rec);
    });
  }

  /** Original (unfiltered) bars per waterfall widget, stashed once when the first
   *  filtered recompute lands so clearing the filter restores instantly. */
  private waterfallOriginalBars = new Map<string, unknown[]>();
  /** Monotonic per-widget sequence so a slow recompute response can never
   *  clobber a newer filter state (or a restore) that raced past it. */
  private waterfallFilterSeq = new Map<string, number>();

  /** Re-run a waterfall widget's persisted compute payload with the active
   *  cross-filter attached and redraw the bridge from the response. Best-effort:
   *  if the backend ignores/errs on `filters`, the current bars stay untouched. */
  private recomputePublishedWaterfall(rec: PublishedWidgetRec, filter: { column: string; values: string[] }): void {
    const widget: any = rec.widget || {};
    const seq = (this.waterfallFilterSeq.get(rec.id) ?? 0) + 1;
    this.waterfallFilterSeq.set(rec.id, seq);
    const payload = {
      ...(widget.payload as Record<string, unknown>),
      filters: [{ column: filter.column, operator: 'IN', values: filter.values }],
    };
    this.subs.add(
      this.http.post<Record<string, unknown>>(this.computeUrlForWidget(widget), payload).subscribe({
        next: (response) => {
          if (this.waterfallFilterSeq.get(rec.id) !== seq) return; // stale response
          // The compute API may answer a bare bars array or {data: bars} — wrap it
          // into the entry shape the shared recompute builder consumes.
          const entry = this.normalizeComputeEntry(widget, Array.isArray(response) ? { data: response } : response);
          const fields = buildRecomputedWidgetFields(entry, {
            widgetId: rec.id,
            title: widget.title,
            format: widget.format as Record<string, unknown> | undefined,
            payload: widget.payload as Record<string, unknown> | undefined,
          });
          const bars = fields?.['waterfallBars'];
          if (!Array.isArray(bars) || !bars.length) return; // nothing usable — keep current view
          const current = this.widgets.find((r) => r.id === rec.id);
          if (!current) return;
          // Stash the originals ONCE (first filtered redraw) for instant restore.
          if (!this.waterfallOriginalBars.has(rec.id)) {
            this.waterfallOriginalBars.set(rec.id, (current.widget as any)?.waterfallBars ?? []);
          }
          this.widgets = this.widgets.map((r) =>
            r.id === rec.id ? { ...r, widget: { ...r.widget, ...fields } } : r,
          );
        },
        // Best-effort: a failed recompute leaves the widget's current bars alone.
        error: () => undefined,
      }),
    );
  }

  /** Put a waterfall widget back on its unfiltered bars (kept from before the
   *  first filtered recompute). Also invalidates any in-flight recompute. */
  private restorePublishedWaterfall(rec: PublishedWidgetRec): void {
    this.waterfallFilterSeq.set(rec.id, (this.waterfallFilterSeq.get(rec.id) ?? 0) + 1);
    const original = this.waterfallOriginalBars.get(rec.id);
    if (!original) return; // never showed filtered bars
    this.waterfallOriginalBars.delete(rec.id);
    this.widgets = this.widgets.map((r) =>
      r.id === rec.id
        ? {
            ...r,
            widget: {
              ...r.widget,
              waterfallBars: original,
              option: buildWaterfallOption(original as any[], (r.widget as any)?.format),
            },
          }
        : r,
    );
  }

  // ── Dropdown → client-side chart filtering (published) ─────────────────────

  /** Active dropdown selections, keyed by the dropdown widget's componentId, so
   *  SEVERAL dropdowns compose: each re-apply starts from every client-side
   *  widget's ORIGINAL rows and applies all active filters in turn. */
  private activeDropdownFilters = new Map<string, { field: string; values: string[] }>();

  /** Filter the client-side chart family (bar-column / line-area / scatter-
   *  bubble) locally from the captured original rows — the compute API has no
   *  usable recompute for these types. A widget ignores filters whose field its
   *  rows don't carry; with no active filters it's restored to its full rows. */
  private applyDropdownFiltersToClientWidgets(): void {
    const filters = [...this.activeDropdownFilters.values()].filter((f) => f.field && f.values.length);
    this.widgets = this.widgets.map((rec) => {
      if (!this.isCrossFilterWidget(rec.widget)) return rec;
      // Seed the original snapshot if load-time capture missed this widget.
      if (!this.crossFilterOriginalRows.has(rec.id)) {
        this.crossFilterOriginalRows.set(rec.id, [...(rec.widget.staticData as Array<Record<string, unknown>>)]);
      }
      const original = this.crossFilterOriginalRows.get(rec.id) ?? [];
      let rows = original;
      filters.forEach(({ field, values }) => {
        if (!original.some((row) => field in row)) return;
        const wanted = new Set(values.map(String));
        rows = rows.filter((row) => wanted.has(String(row[field] ?? '')));
      });
      // New widget/staticData refs so the chart's ngOnChanges fires.
      return { ...rec, widget: { ...rec.widget, staticData: rows } };
    });
  }

  /** Snapshot each cross-filter widget's original staticData rows (once, on load)
   *  so a filter can always be reversed. Called after widgets are set. */
  private captureCrossFilterOriginals(): void {
    this.crossFilterOriginalRows.clear();
    this.widgets.forEach((rec) => {
      if (this.isCrossFilterWidget(rec.widget)) {
        this.crossFilterOriginalRows.set(rec.id, [...(rec.widget.staticData as Array<Record<string, unknown>>)]);
      }
    });
  }

  /** Table & Matrix tiles render from their baked-in snapshot (staticData /
   *  drilldownData) — they must NOT be POSTed to the chart compute API on open
   *  (a plain table payload 400s there). Excluded from compute-on-open and from
   *  being a navigation-filter recompute target. */
  private isTableMatrixWidget(widget: any): boolean {
    return String(widget?.type ?? '').toLowerCase() === 'table-matrix';
  }

  /** Widgets that render purely from their published snapshot and must never be
   *  marked 'loading' for the on-open recompute — nothing would ever clear it:
   *  table-matrix (snapshot rows; its payload 400s on the chart compute API)
   *  and file-upload (an action tile with no compute payload at all). */
  private isSnapshotOnlyWidget(widget: any): boolean {
    return this.isTableMatrixWidget(widget) || String(widget?.type ?? '').toLowerCase() === 'file-upload';
  }

  /** Input controls (Slider / Input Box / Dropdown / File Upload) take NO
   *  card-header formatting (by request). Gating the CSS-var bindings on this
   *  (rather than only not offering the controls in the builder) means a stale
   *  `format` published while the feature was briefly enabled can never paint
   *  those tiles. */
  /** Re-apply a published group card's own design (Format popup in the builder:
   *  border colour/width, card background, corner radius, transparent) as inline
   *  styles. Mirrors DvmLayoutBuilderComponent.groupCardStyleMap so the card
   *  looks the same either side of publish. Empty for every other widget type. */
  groupCardStyles(widget: any): Record<string, string> {
    if (String(widget?.type ?? '') !== 'group-card') return {};
    const format = widget?.format as Record<string, unknown> | undefined;
    const styles: Record<string, string> = {};
    if (!format) return styles;
    const transparent = format['cardTransparent'] === true || format['cardTransparent'] === 'true';
    const border = String(format['cardBorderColor'] ?? '').trim();
    const width = Number(format['cardBorderWidth']);
    const background = String(format['cardBackground'] ?? '').trim();
    const radius = Number(format['cardCornerRadius']);

    if (transparent) {
      styles['background'] = 'transparent';
      styles['border-color'] = 'transparent';
      styles['box-shadow'] = 'none';
    } else {
      if (background) styles['background'] = background;
      if (border) styles['border-color'] = border;
    }
    if (Number.isFinite(width)) styles['border-width'] = `${Math.max(0, width)}px`;
    if (Number.isFinite(radius)) styles['border-radius'] = `${Math.max(0, radius)}px`;
    return styles;
  }

  takesHeaderFormat(widget: any): boolean {
    const type = String(widget?.type ?? '').toLowerCase();
    return type !== 'slider' && type !== 'input-box' && type !== 'dropdown' && type !== 'file-upload';
  }

  /** Pull flat rows out of a compute/upload result for a table target (common
   *  envelope shapes: data / Data / rows / nested data.data / top-level array). */
  private extractTableRows(entry: Record<string, unknown>): Array<Record<string, unknown>> {
    if (Array.isArray(entry)) return entry as Array<Record<string, unknown>>;
    const candidate = entry['data'] ?? entry['Data'] ?? entry['rows'];
    if (Array.isArray(candidate)) return candidate as Array<Record<string, unknown>>;
    const nested = (candidate as { data?: unknown } | undefined)?.data;
    return Array.isArray(nested) ? (nested as Array<Record<string, unknown>>) : [];
  }

  private isKpiWidget(widget: any): boolean {
    return String(widget?.type ?? '').toLowerCase() === 'kpi-card';
  }

  private isSliderWidget(widget: any): boolean {
    return String(widget?.type ?? '').toLowerCase() === 'slider';
  }
  private isVisualChartWidget(widget: any): boolean {
    return this.isChartWidget(widget) && !this.isKpiWidget(widget);
  }
  private layoutTypeForWidget(widget: any): string {
    const type = String(widget?.type ?? '').toLowerCase();
    if (type === 'waterfall-chart') return 'WATERFALL';
    if (type === 'drill-down') return 'DRILL_DOWN_TABLE';
    if (type === 'horizontal-bar-chart') return 'HORIZONTAL_BAR_CHART';
    if (type === 'kpi-card') return 'KPI';
    if (type === 'bar-column') return 'BAR';
    if (type === 'line-area') return 'LINE_AREA';
    if (type === 'scatter-bubble') return 'SCATTER_BUBBLE';
    if (type === 'dropdown') return 'DROPDOWN';
    if (type === 'table-matrix') return 'TABLE_MATRIX';
    if (type === 'file-upload') return 'FILE_UPLOAD';
    return String(widget?.layoutType ?? widget?.payload?.chartType ?? '').toUpperCase();
  }

  private minColSpanForItem(id: string): number {
    const rec = this.widgets.find((item) => item.id === id);
    return this.minColSpanForWidget(rec?.widget);
  }

  private minRowSpanForItem(id: string): number {
    const rec = this.widgets.find((item) => item.id === id);
    return this.minRowSpanForWidget(rec?.widget);
  }

  private minColSpanForWidget(widget: any): number {
    if (this.isKpiWidget(widget)) return 1;
    if (this.isSliderWidget(widget)) return 2;
    // A group card is a CONTAINER (usually of narrow input controls), not a
    // chart — without this branch it inherits the visual-chart minimum
    // (3 cols / 4 rows) and its width silently stops reducing at that floor,
    // both in the edge-drag resize clamp and the on-load colSpan re-clamp.
    if (this.isGroupCardWidget(widget)) return 2;
    return this.isVisualChartWidget(widget) ? 3 : 2;
  }

  private minRowSpanForWidget(widget: any): number {
    if (String(widget?.type ?? '').toLowerCase() === 'dropdown') return 1;
    if (this.isKpiWidget(widget)) return 1;
    if (this.isSliderWidget(widget)) return 2;
    if (this.isGroupCardWidget(widget)) return 2;
    return this.isVisualChartWidget(widget) ? 4 : 2;
  }

  private isGroupCardWidget(widget: any): boolean {
    return String(widget?.type ?? '').toLowerCase() === 'group-card';
  }

  private buildSavedDashboardLayout(layoutJson: Record<string, unknown>) {
    return {
      columns: Number((layoutJson['layout'] as Record<string, unknown> | undefined)?.['columns'] ?? 12),
      items: this.layout.map((item, index) => ({ ...item, order: index })),
      pageContent: layoutJson['pageContent'],
    };
  }
  private async resolvePublishApi(layoutJson: Record<string, unknown>): Promise<string> {
    const fromLayout = String((layoutJson['apiEndpoints'] as Record<string, unknown> | undefined)?.['publishApi'] ?? '').trim();
    if (fromLayout) return resolveSelfServiceApiUrl(fromLayout);
    try {
      const config = await firstValueFrom(this.http.get<Record<string, unknown>>('/assets/self-service-config/dvm-layout-builder.json'));
      return resolveSelfServiceApiUrl(String((config?.['apiEndpoints'] as Record<string, unknown> | undefined)?.['publishApi'] ?? '').trim());
    } catch {
      return '';
    }
  }

  /** `uuid` present → this dashboard already has a `dashboard_layouts` row owned
   *  by this user, so re-saving must `update` that row instead of `insert`ing a
   *  duplicate one (publish/save were always `insert`, which is why re-saving
   *  the same dashboard kept appending new rows — see
   *  SelfServiceDashboardService.findOwnedDashboardLayoutRecord). */
  private buildPublishEnvelope(layoutJson: Record<string, unknown>, emailId: string, dashboardId: string, uuid: string | null = null): Record<string, unknown> {
    return {
      requestData: {
        email_id: emailId,
        role: 'User',
        layout_json: layoutJson,
        dashboard_id: dashboardId,
        ...(uuid ? { uuid } : {}),
      },
      // `operation` sits at the TOP (envelope) level, not inside requestData.
      operation: uuid ? 'update' : 'insert',
      responseList: [],
      entity: 'dashboard_layouts',
      batchId: 'test',
      workflowId: 'ABC-123',
      endpoints: [],
      version: '1.0',
    };
  }
  private buildPersistedLayoutJson(): Record<string, unknown> | null {
    if (!this.originalLayoutJson) return null;
    const next = this.deepCloneRecord(this.originalLayoutJson);
    const layoutById = new Map(this.layout.map((item, index) => [item.id, { ...item, index }]));
    const widgetById = new Map(this.widgets.map((rec) => [rec.id, rec.widget]));
    const count = new Map<string, number>();

    const visit = (node: any): void => {
      if (!node || typeof node !== 'object') return;
      if (node.type) {
        const id = this.resolveNodePublishedId(node, count);
        const liveWidget = widgetById.get(id);
        if (liveWidget) {
          const snapshot = this.serializableWidgetSnapshot(liveWidget);
          Object.keys(node).forEach((key) => delete node[key]);
          Object.assign(node, snapshot);
        }
        const layout = layoutById.get(id);
        if (layout) {
          node.layout = { ...(node.layout || {}), colSpan: layout.colSpan, rowSpan: layout.rowSpan, x: layout.x, y: layout.y, order: layout.index, customSize: !!layout.customSize };
          node.class = `col-${layout.colSpan}`;
          node.height = layout.rowSpan * 48;
        }
        return;
      }
      if (Array.isArray(node.columns)) this.patchAndSortNodeList(node.columns, visit, layoutById, count);
      if (Array.isArray(node.rows)) this.patchAndSortNodeList(node.rows, visit, layoutById, count);
    };

    if (Array.isArray(next['pageContent'])) {
      this.patchAndSortNodeList(next['pageContent'] as any[], visit, layoutById, count);
    }

    // Per-node `layout.colSpan/rowSpan` (set above) is what dashboard.component.ts's
    // own buildDefaultLayout() needs to reconstruct x/y/w/h for every breakpoint on
    // load — but also mirror the SAME {id,colSpan,rowSpan,order} list here, keyed by
    // breakpoint (single "xl" breakpoint for now, this view has no responsive
    // breakpoints of its own), so `layout_json.layout` carries a breakpoint-keyed
    // snapshot alongside the plain column count, matching the shape other DVM
    // dashboard layouts persist.
    const existingLayout = (next['layout'] as Record<string, unknown> | undefined) ?? {};
    const layoutItems = this.layout.map((item, index) => {
      const rec = this.widgets.find((widget) => widget.id === item.id);
      return {
        id: item.id,
        order: index,
        colSpan: item.colSpan,
        rowSpan: item.rowSpan,
        x: item.x,
        y: item.y,
        componentId: String(rec?.widget?.componentId ?? ''),
        customSize: !!item.customSize,
      };
    });
    next['layout'] = {
      ...existingLayout,
      columns: Number(existingLayout['columns'] ?? 12),
      items: layoutItems,
      breakpoints: {
        ...((existingLayout['breakpoints'] as Record<string, unknown> | undefined) ?? {}),
        xl: layoutItems.map(({ id, colSpan, rowSpan, x, y, order, customSize }) => ({
          id,
          colSpan,
          rowSpan,
          x,
          y,
          order,
          customSize,
        })),
      },
    };

    const now = new Date().toISOString();
    next['updatedAt'] = now;
    next['publishedAt'] = now;
    next['status'] = 'published';
    return next;
  }

  private patchAndSortNodeList(
    list: any[],
    visit: (node: any) => void,
    _layoutById: Map<string, PublishedLayoutItem & { index: number }>,
    _count: Map<string, number>,
  ): void {
    list.forEach((node) => visit(node));
    if (!list.every((node) => node && typeof node === 'object' && node.type)) return;
    list.sort((a, b) => {
      const aOrder = Number(a?.layout?.order ?? Number.MAX_SAFE_INTEGER);
      const bOrder = Number(b?.layout?.order ?? Number.MAX_SAFE_INTEGER);
      return aOrder - bOrder;
    });
  }

  private serializableWidgetSnapshot(widget: any): Record<string, unknown> {
    const { onWidgetValueChange: _onWidgetValueChange, ...snapshot } = (widget || {}) as Record<string, unknown>;
    void _onWidgetValueChange;
    return toSerializable(snapshot);
  }

  private resolveNodePublishedId(node: any, count: Map<string, number>): string {
    const base = String(node?.widgetId ?? node?.id ?? `widget-${this.nextNodeOrdinal(count)}`).replace(/[^a-zA-Z0-9-_]/g, '-');
    const idx = (count.get(base) ?? 0) + 1;
    count.set(base, idx);
    return idx === 1 ? base : `${base}__${idx}`;
  }

  private nextNodeOrdinal(count: Map<string, number>): number {
    return Array.from(count.values()).reduce((sum, value) => sum + value, 0) + 1;
  }

  private deepCloneRecord(value: Record<string, unknown>): Record<string, unknown> {
    // NOT structuredClone: it throws on any function value, and stored chart
    // configs can carry one (e.g. an echarts tooltip/label `formatter`
    // callback) — toSerializable strips those (and breaks cycles) instead of
    // throwing, matching what JSON.stringify does silently at publish time.
    return toSerializable(value);
  }

  private createDashboardId(): string {
    return `dvm-dashboard-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  }
  private notifyChartsAboutResize(): void {
    window.requestAnimationFrame(() => window.dispatchEvent(new Event('resize')));
  }
  private classToColSpan(value: unknown): number | null {
    const match = String(value ?? '').match(/col-(\d+)/);
    return match ? Number(match[1]) : null;
  }


  private buildLayoutFromJson(
    layoutJson: Record<string, unknown> | null,
    widgets: PublishedWidgetRec[],
  ): PublishedLayoutItem[] {
    const saved = this.extractSavedLayoutItems(layoutJson, widgets);
    return saved.length ? saved : this.buildLayoutFromWidgets(widgets);
  }

  private extractSavedLayoutItems(
    layoutJson: Record<string, unknown> | null,
    widgets: PublishedWidgetRec[],
  ): PublishedLayoutItem[] {
    const layout = layoutJson?.['layout'] as Record<string, unknown> | undefined;
    const breakpoints = layout?.['breakpoints'] as Record<string, unknown> | undefined;
    const layoutItems = Array.isArray(layout?.['items'])
      ? layout['items'] as Array<Record<string, unknown>>
      : [];
    const rawItems = Array.isArray(breakpoints?.['xl'])
      ? breakpoints['xl'] as Array<Record<string, unknown>>
      : layoutItems;
    if (!rawItems.length) return [];

    const componentIdByLayoutId = new Map(
      layoutItems
        .map((item) => [String(item?.['id'] ?? '').trim(), String(item?.['componentId'] ?? '').trim()] as const)
        .filter(([id, componentId]) => !!id && !!componentId)
    );
    const widgetById = new Map(widgets.map((rec) => [rec.id, rec]));
    const widgetByComponentId = new Map(
      widgets
        .map((rec) => [String(rec.widget?.componentId ?? '').trim(), rec] as const)
        .filter(([componentId]) => !!componentId)
    );
    const seen = new Set<string>();
    const restored: Array<PublishedLayoutItem & { order: number }> = [];

    // Free-form placement is explicit x/y per item — older saved dashboards
    // (published before this) only ever carried colSpan/rowSpan/order, no
    // position, so detect which shape we're restoring rather than assuming.
    const hasExplicitPositions = rawItems.some((record) => Number.isFinite(Number(record?.['x'])) && Number.isFinite(Number(record?.['y'])));

    rawItems.forEach((record, fallbackOrder) => {
      const recordId = String(record?.['id'] ?? '').trim();
      const componentId = String(record?.['componentId'] ?? componentIdByLayoutId.get(recordId) ?? '').trim();
      const rec = widgetById.get(recordId) ?? widgetByComponentId.get(componentId);
      if (!rec || seen.has(rec.id)) return;

      seen.add(rec.id);
      const customSize = record['customSize'] === true || rec.widget?.layout?.customSize === true;
      restored.push({
        id: rec.id,
        colSpan: this.clamp(Number(record['colSpan'] ?? rec.widget?.layout?.colSpan ?? this.classToColSpan(rec.widget?.class) ?? 6), this.minColSpanForWidget(rec.widget), 12),
        rowSpan: this.clamp(Number(record['rowSpan'] ?? rec.widget?.layout?.rowSpan ?? Math.ceil(Number(rec.widget?.height ?? 384) / 48)), this.minRowSpanForWidget(rec.widget), 18),
        x: hasExplicitPositions ? Math.max(0, Number(record['x'] ?? 0)) : 0,
        y: hasExplicitPositions ? Math.max(0, Number(record['y'] ?? 0)) : 0,
        customSize,
        order: Number(record['order'] ?? fallbackOrder),
      });
    });

    restored.sort((a, b) => a.order - b.order);

    // Legacy layout (order only, no x/y) — derive positions with the same
    // row-fill flow a brand-new dashboard gets, so it still renders sensibly
    // instead of every tile collapsing onto (0,0).
    const positioned = hasExplicitPositions ? restored : this.assignFlowPositions(restored, 0);

    // Any widget on the dashboard that ISN'T in the saved layout (added since
    // the last publish) needs a position too — start it below everything
    // already placed so it can't land on top of an existing tile regardless
    // of that tile's own x/y. EXCEPT group-card children: extractWidgets()
    // flattens them into `widgets` too (so compute/target-matching can find a
    // grouped chart like any other), but they were NEVER in the saved
    // layout.items to begin with (only the group-card itself is) — without
    // this exclusion they'd all fall into "not in the saved layout" and get
    // their own standalone grid position, rendering every grouped chart a
    // SECOND time as its own separate top-level tile.
    const startY = positioned.length ? Math.max(...positioned.map((item) => item.y + item.rowSpan)) : 0;
    const groupChildIds = this.groupChildWidgetIds(widgets);
    const newWidgets = widgets.filter((rec) => !seen.has(rec.id) && !groupChildIds.has(rec.id));
    const appended = this.assignFlowPositions(
      newWidgets.map((rec) => this.widgetToLayoutSeed(rec)),
      startY,
    );

    return [...positioned, ...appended].map(({ order: _order, ...item }) => item);
  }

  /** Row-fill flow layout (left→right, wrapping at 12 columns, using the
   *  tallest tile in a row to know where the next row starts) — the same
   *  simple algorithm a brand-new dashboard uses to arrange tiles before the
   *  user does any free-form dragging, and the fallback for legacy saved
   *  layouts that only ever recorded an order, not a position. */
  private assignFlowPositions<T extends { colSpan: number; rowSpan: number }>(
    items: T[],
    startY: number,
  ): Array<T & { x: number; y: number }> {
    let cursorX = 0;
    let cursorY = startY;
    let rowHeight = 0;
    return items.map((item) => {
      if (cursorX + item.colSpan > 12) {
        cursorX = 0;
        cursorY += rowHeight;
        rowHeight = 0;
      }
      const placed = { ...item, x: cursorX, y: cursorY };
      cursorX += item.colSpan;
      rowHeight = Math.max(rowHeight, item.rowSpan);
      return placed;
    });
  }

  /** Span-only fields for a widget, before a position is assigned — shared by
   *  `buildLayoutFromWidgets` (no saved layout at all) and the "newly added
   *  widget not in the saved layout" path above. */
  private widgetToLayoutSeed(rec: PublishedWidgetRec): { id: string; colSpan: number; rowSpan: number; customSize: boolean; order: number } {
    const customSize = rec.widget?.layout?.customSize === true;
    const isDropdown = String(rec.widget?.type ?? '').toLowerCase() === 'dropdown';
    // Older dropdowns were published with the generic input/card height of
    // four rows. Compact them automatically until the user explicitly
    // resizes the control in Edit Layout; that custom size is then persisted.
    const rowSpan = isDropdown && !customSize
      ? 1
      : Number(rec.widget?.layout?.rowSpan ?? Math.ceil(Number(rec.widget?.height ?? 384) / 48));
    return {
      id: rec.id,
      colSpan: this.clamp(Number(rec.widget?.layout?.colSpan ?? this.classToColSpan(rec.widget?.class) ?? 6), this.minColSpanForWidget(rec.widget), 12),
      rowSpan: this.clamp(rowSpan, this.minRowSpanForWidget(rec.widget), 18),
      customSize,
      order: 0,
    };
  }

  /** The layout's single source of truth when there's no saved layout at all
   *  (brand-new dashboard) — each widget's own `layout.colSpan/rowSpan`
   *  (server-persisted by saveLayout()'s publish, or the dashboard builder's
   *  Generate/Save), flowed into positions left-to-right/top-to-bottom. */
  private buildLayoutFromWidgets(widgets: PublishedWidgetRec[]): PublishedLayoutItem[] {
    // Same exclusion as extractSavedLayoutItems: group-card children are only
    // ever rendered NESTED inside their card, never as their own top-level tile.
    const groupChildIds = this.groupChildWidgetIds(widgets);
    const topLevel = widgets.filter((rec) => !groupChildIds.has(rec.id));
    return this.assignFlowPositions(topLevel.map((rec) => this.widgetToLayoutSeed(rec)), 0)
      .map(({ order: _order, ...item }) => item);
  }

  /** Every widget id that lives INSIDE a group-card (its `children`), across
   *  every group-card on the dashboard — used to keep them out of the
   *  top-level layout-positioning paths above (they render nested, via
   *  `childWidgetIds` + widgetRecById() in the template, not as their own tile). */
  private groupChildWidgetIds(widgets: PublishedWidgetRec[]): Set<string> {
    const ids = new Set<string>();
    widgets.forEach((rec) => {
      if (rec.widget?.type === 'group-card' && Array.isArray(rec.widget?.childWidgetIds)) {
        (rec.widget.childWidgetIds as string[]).forEach((id) => ids.add(id));
      }
    });
    return ids;
  }

  private async resolveCurrentEmail(): Promise<string> {
    if (this.resolvedEmailMemo) return this.resolvedEmailMemo;
    let email = '';
    try {
      if (await this.keycloak.isLoggedIn()) {
        const profile = await this.keycloak.loadUserProfile();
        email = profile.email || '';
      }
    } catch { /* fall through */ }
    if (email) this.resolvedEmailMemo = email;
    return email;
  }

  private confirmOwnership(email: string): void {
    this.isOwner = this.dashboardService.isOwnedDashboardSlug(this.slug);
    this.syncToolbarState();
    if (this.isOwner || !email) return;
    this.subs.add(
      this.dashboardService.isDashboardOwnedByCurrentUser(this.slug, email).subscribe((owned) => {
        this.isOwner = owned;
        this.syncToolbarState();
      }),
    );
  }

  private clamp(value: number, min: number, max: number): number {
    const next = Number.isFinite(value) ? Math.round(value) : min;
    return Math.min(max, Math.max(min, next));
  }
}








